/**
 * Typed wrapper for calling Station edge functions.
 *
 * Always sends:
 *   - apikey: <publishable key>  (sb_publishable_... — NOT a JWT, so it must
 *     never go in `Authorization: Bearer`; the gateway rejects non-JWT Bearers)
 *   - x-customer-token: <session_token>  (when one is set)
 *   - Content-Type: application/json
 *
 * Returns the parsed JSON body. Throws an Error with the server's `error`
 * field as message + a `status` property on the error if the response is
 * not 2xx.
 */

import { SUPABASE_PUBLISHABLE_KEY, FUNCTIONS_BASE, SUPABASE_URL } from "./supabase";
import { normalizeEmailStats, type EmailStats } from "./emailStats";
import { getStoredToken } from "./auth";
import { readAllRows } from "./readAllRows";
import { quoteFilterList } from "./postgrestFilter";
import type { PendingDay } from "./bankRun";
import type {
  DrinkDetail,
  DrinkListResponse,
  FilterKind,
} from "../types/drink";

export class ApiError extends Error {
  status: number;
  /** A multi-request save persisted data but a later operation failed. */
  saved?: boolean;
  /** The record changed after the screen loaded it; nothing was written. Refresh first. */
  changed?: boolean;
  /** Another save for the same record was still in flight; nothing was written. Retry shortly. */
  busy?: boolean;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/** Default upper bound on every edge function call. A hung fetch (flaky bar
 * Wi-Fi) used to leave loading states stuck forever with no retry; this
 * surfaces an ApiError(408) the caller can choose to handle. */
const DEFAULT_TIMEOUT_MS = 20_000;

export async function callFn<T = unknown>(
  fnName: string,
  body: object | null = null,
  options: { method?: "POST" | "GET"; tokenOverride?: string | null; timeoutMs?: number } = {}
): Promise<T> {
  const token = options.tokenOverride ?? getStoredToken();
  const headers: Record<string, string> = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  if (token) headers["x-customer-token"] = token;

  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let res: Response;
  try {
    res = await fetch(`${FUNCTIONS_BASE}/${fnName}`, {
      method: options.method ?? "POST",
      headers,
      body: body ? JSON.stringify(body) : null,
      signal: controller.signal,
    });
  } catch (err: any) {
    clearTimeout(timer);
    if (err?.name === "AbortError") {
      throw new ApiError(`${fnName} timed out after ${Math.round(timeoutMs / 1000)}s`, 408);
    }
    throw err;
  }
  clearTimeout(timer);

  let parsed: any = null;
  try {
    parsed = await res.json();
  } catch {
    /* ignore parse errors — handled below */
  }

  if (!res.ok) {
    const message = parsed?.error || `${fnName} failed (HTTP ${res.status})`;
    const error = new ApiError(message, res.status);
    if (parsed?.saved === true) error.saved = true;
    if (parsed?.changed === true) error.changed = true;
    if (parsed?.busy === true) error.busy = true;
    throw error;
  }
  return parsed as T;
}

export async function callRpc<T = unknown>(
  rpcName: string,
  body: object | null = null
): Promise<T> {
  const token = getStoredToken();
  const headers: Record<string, string> = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  if (token) headers["x-customer-token"] = token;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${rpcName}`, {
    method: "POST",
    headers,
    body: body ? JSON.stringify(body) : null,
  });

  if (!res.ok) {
    let parsed: any = null;
    try { parsed = await res.json(); } catch {}
    const message = parsed?.message || parsed?.error || `${rpcName} failed (HTTP ${res.status})`;
    throw new ApiError(message, res.status);
  }
  return res.json() as Promise<T>;
}

// ── Phase 2: drink read APIs ──

export function listDrinks(filter: FilterKind = "all"): Promise<DrinkListResponse> {
  return callFn<DrinkListResponse>("bar-list-drinks", { filter });
}

export function searchDrinks(q: string): Promise<DrinkListResponse> {
  return callFn<DrinkListResponse>("bar-search-drinks", { q });
}

export function getDrink(slug: string): Promise<DrinkDetail> {
  return callFn<DrinkDetail>("bar-get-drink", { slug });
}

/**
 * Best-effort activity logger. Increments today's per-drink counter for the
 * given action ("load" when the recipe is opened, "search" for search-result
 * loads, "pin" for manual pin events). Failures are swallowed — activity
 * logging must never block the user.
 */
export function logActivity(
  slug: string,
  action: "load" | "search" | "pin"
): Promise<void> {
  return callFn("bar-log-activity", { slug, action }).then(
    () => undefined,
    () => undefined,
  );
}

// ── Phase 5: admin APIs (bar_manager+ for recipe ones, bar_owner for staff/tablets) ──

import type {
  AdminDrinkListItem,
  AdminRoom,
  AdminStaffMember,
  AdminStatusFilter,
  AdminTablet,
  AdminVersionsResponse,
} from "../types/admin";
import type { BarRole } from "../types/auth";

interface AdminDrinksResponse {
  drinks: AdminDrinkListItem[];
  count: number;
  status_filter: string;
}

export function adminListDrinks(
  status_filter: AdminStatusFilter = "all",
  q: string = ""
): Promise<AdminDrinksResponse> {
  return callFn<AdminDrinksResponse>("bar-admin-list-drinks", { status_filter, q });
}

export function adminGetVersions(slug: string): Promise<AdminVersionsResponse> {
  return callFn<AdminVersionsResponse>("bar-admin-get-drink-versions", { slug });
}

interface AdminStaffResponse {
  staff: AdminStaffMember[];
  count: number;
}

export function adminListStaff(): Promise<AdminStaffResponse> {
  return callFn<AdminStaffResponse>("bar-admin-list-staff", null);
}

export function adminGrantRole(
  customer_id: string,
  role: BarRole,
  grant: boolean
): Promise<{ ok: true; staff_roles: string[]; no_op: boolean }> {
  return callFn("bar-admin-grant-role", { customer_id, role, grant });
}

interface AdminTabletsResponse {
  tablets: AdminTablet[];
  count: number;
  rooms: AdminRoom[];
}

export function adminListTablets(): Promise<AdminTabletsResponse> {
  return callFn<AdminTabletsResponse>("bar-admin-list-tablets", null);
}

export function adminCreateTablet(payload: {
  request_id: string;
  label: string;
  room_id: string | null;
}): Promise<{ ok: true; tablet_customer_id: string; label: string; room_id: string | null }> {
  return callFn("bar-admin-create-tablet", payload);
}

export function adminUpdateRooms(
  rooms: Array<{ id?: string; slug?: string; name: string; sort_order?: number }>,
): Promise<{ ok: true; rooms: AdminRoom[] }> {
  return callFn("bar-admin-update-rooms", { rooms });
}

export function adminUpdateTabletMeta(payload: {
  tablet_customer_id: string;
  label?: string;
  room_id?: string | null;
  sort_order?: number;
}): Promise<{ ok: true; meta: { id: string; label: string; room_id: string | null; sort_order: number } }> {
  return callFn("bar-admin-update-tablet-meta", payload);
}

export function flagIngredientOut(payload: {
  ingredient_id: string;
  note?: string;
}): Promise<{
  ok: true;
  ingredient_id: string;
  ingredient_name: string;
  cascade_applied: boolean;
  cascaded_drink_count: number;
  sms: { targets: number; delivered: number; errors: string[] };
}> {
  return callFn("bar-flag-ingredient-out", payload);
}

export function adminIssueTabletToken(
  tablet_customer_id: string
): Promise<{ ok: true; token: string; expires_at: string; tablet_email: string; tablet_label: string }> {
  return callFn("bar-admin-issue-tablet-token", { tablet_customer_id });
}

export function adminRevokeTabletToken(
  tablet_customer_id: string
): Promise<{ ok: true; revoked_count: number }> {
  return callFn("bar-admin-revoke-tablet-token", { tablet_customer_id });
}

// ── Phase 6: editor APIs (bar_lead+) ──

export interface AdminIngredientMaster {
  id: string;
  name: string;
  category: string;
  default_unit: string;
  available: boolean;
  is_untracked?: boolean;
  allergen_flags: string[];
  notes: string | null;
  is_retail_bottle?: boolean;
}

export interface RecipeIngredientOption {
  id: string;
  source: "ingredient" | "menu_item" | "catalog_item";
  name: string;
  category: string;
  default_unit: string;
  available: boolean;
  is_untracked: boolean;
}

export function adminSearchRecipeIngredients(query: string, offset = 0): Promise<{
  items: RecipeIngredientOption[];
  has_more: boolean;
}> {
  return callFn("bar-admin-search-recipe-ingredients", { query, offset });
}

export function adminListIngredients(): Promise<{
  ingredients: AdminIngredientMaster[];
  count: number;
}> {
  return callFn("bar-admin-list-ingredients", null);
}

export function adminCreateDrink(
  name: string,
  category: string = "Whiskey"
): Promise<{ ok: true; drink_id: string; slug: string; version_id: string }> {
  return callFn("bar-admin-create-drink", { name, category });
}

export interface AdminEditorVersion {
  id: string;
  drink_id: string;
  version_number: string;
  status: string;
  glass: string | null;
  ice: string | null;
  method: string | null;
  garnish: string | null;
  smoked: boolean;
  frozen: boolean;
  vessel: string | null;
  instructions: string[];
  staff_notes: string | null;
  internal_admin_notes: string | null;
  testing_notes: string | null;
  warning: string | null;
  change_summary: string | null;
  approved_at: string | null;
  approved_by: string | null;
  created_at: string;
  created_by: string | null;
}

export interface AdminEditorDrink {
  id: string;
  slug: string;
  name: string;
  category: string;
  service_status: string;
  manual_featured_today_until: string | null;
  tags: string[];
  active_approved_version_id: string | null;
}

export interface AdminEditorIngredientRow {
  id: string;
  sort_order: number;
  amount: number;
  unit: string;
  scale_type:
    | "exact"
    | "do_not_scale"
    | "per_cocktail"
    | "approximate"
    | "garnish_each"
    | "batch_only";
  display_name: string | null;
  prep_recipe_id: string | null;
  ingredient_id?: string | null;
  menu_item_id?: string | null;
  catalog_item_id?: string | null;
  ingredient: {
    id: string;
    name: string;
    category: string;
    default_unit: string;
    available: boolean;
    is_untracked?: boolean;
  } | null;
}

export interface AdminEditorFinishStepRow {
  id: string;
  sort_order: number;
  finish_type:
    | "Garnish"
    | "Smoke"
    | "Cream Top"
    | "Foam"
    | "Float"
    | "Rim"
    | "Mist"
    | "Expressed Citrus"
    | "Topper"
    | "Serveware"
    | "Presentation"
    | "Other";
  label: string;
  instruction: string | null;
  quantity: number | null;
  unit: string | null;
  prep_recipe_id: string | null;
  scale_type: "per_cocktail" | "do_not_scale" | "optional" | "batch_finish";
  required: boolean;
}

export interface AdminGetVersionResponse {
  drink: AdminEditorDrink;
  version: AdminEditorVersion;
  ingredients: AdminEditorIngredientRow[];
  finish_steps: AdminEditorFinishStepRow[];
  aliases: string[];
}

export function adminGetVersion(
  version_id: string
): Promise<AdminGetVersionResponse> {
  return callFn<AdminGetVersionResponse>("bar-admin-get-version", { version_id });
}

// ── Phase 7: approve / retire / restore / history ──

export function adminApproveVersion(
  version_id: string,
  change_summary: string
): Promise<{ ok: true; drink_id: string; new_approved_id: string; previous_approved_id: string | null }> {
  return callFn("bar-admin-approve-version", { version_id, change_summary });
}

export function adminRetireVersion(
  version_id: string
): Promise<{ ok: true; drink_id: string; version_id: string; was_active: boolean }> {
  return callFn("bar-admin-retire-version", { version_id });
}

export function adminRestoreAsDraft(
  version_id: string
): Promise<{ ok: true; drink_id: string; version_id: string; version_number: string }> {
  return callFn("bar-admin-restore-as-draft", { version_id });
}

export interface AuditLogEntry {
  id: string;
  user_id: string | null;
  user_name: string | null;
  action: string;
  entity_type: string;
  entity_id: string | null;
  before_data: any;
  after_data: any;
  created_at: string;
}

export function adminGetDrinkHistory(
  slug: string
): Promise<{ drink_id: string; history: AuditLogEntry[]; count: number }> {
  return callFn("bar-admin-get-drink-history", { slug });
}

export function adminCreateDraft(
  slug: string
): Promise<{ ok: true; drink_id: string; version_id: string; version_number: string }> {
  return callFn("bar-admin-create-draft", { slug });
}

export interface UpdateDraftPayload {
  version_id: string;
  drink?: {
    name?: string;
    category?: string;
    tags?: string[];
    service_status?: string;
    manual_featured_today_until?: string | null;
  };
  version?: {
    glass?: string | null;
    ice?: string | null;
    method?: string | null;
    garnish?: string | null;
    vessel?: string | null;
    smoked?: boolean;
    frozen?: boolean;
    instructions?: string[];
    staff_notes?: string | null;
    internal_admin_notes?: string | null;
    testing_notes?: string | null;
    warning?: string | null;
    change_summary?: string | null;
    status?: "Draft" | "Testing" | "Needs Review";
  };
  ingredients?: Array<{
    sort_order: number;
    ingredient_id?: string | null;
    menu_item_id?: string | null;
    catalog_item_id?: string | null;
    display_name?: string | null;
    amount: number;
    unit: string;
    scale_type:
      | "exact"
      | "do_not_scale"
      | "per_cocktail"
      | "approximate"
      | "garnish_each"
      | "batch_only";
  }>;
  finish_steps?: Array<{
    sort_order: number;
    finish_type: AdminEditorFinishStepRow["finish_type"];
    label: string;
    instruction?: string | null;
    quantity?: number | null;
    unit?: string | null;
    prep_recipe_id?: string | null;
    scale_type: AdminEditorFinishStepRow["scale_type"];
    required: boolean;
  }>;
  aliases?: string[];
}

export function adminUpdateDraft(
  payload: UpdateDraftPayload
): Promise<{ ok: true; version_id: string; drink_id: string; audit_recorded?: boolean; warning?: string }> {
  return callFn("bar-admin-update-draft", payload);
}

export function adminSubmitForReview(
  version_id: string
): Promise<{ ok: true; status: "Needs Review" }> {
  return callFn("bar-admin-submit-for-review", { version_id });
}

export function adminDiscardDraft(
  version_id: string
): Promise<{ ok: true; drink_also_deleted: boolean }> {
  return callFn("bar-admin-discard-draft", { version_id });
}

// ── Phase 8: service controls + ingredients admin + activity admin + settings ──

export type DrinkServiceStatus =
  | "Active"
  | "Seasonal"
  | "Event Only"
  | "Hidden"
  | "86d"
  | "Retired";

export function adminSetServiceStatus(
  drink_id: string,
  service_status: DrinkServiceStatus
): Promise<{ ok: true; drink_id: string; service_status: string; no_op?: boolean }> {
  return callFn("bar-admin-set-service-status", { drink_id, service_status });
}

export function adminFeatureToday(
  drink_id: string,
  feature: boolean
): Promise<{ ok: true; drink_id: string; manual_featured_today_until: string | null }> {
  return callFn("bar-admin-feature-today", { drink_id, feature });
}

export function adminSetIngredientAvailability(
  ingredient_id: string,
  available: boolean,
  cascade_86: boolean,
  cascade_restore?: boolean
): Promise<{
  ok: true;
  ingredient_id: string;
  available: boolean;
  cascade_drink_count: number;
  cascade_drink_ids: string[];
  restored_drink_count?: number;
  restored_drink_ids?: string[];
  no_op?: boolean;
}> {
  return callFn("bar-admin-set-ingredient-availability", {
    ingredient_id,
    available,
    cascade_86,
    cascade_restore: !!cascade_restore,
  });
}

export interface IngredientImpactRecipe {
  drink_id: string | null;
  slug: string | null;
  name: string;
  service_status: string | null;
  version_id: string;
  version_number: string;
}

export function adminListIngredientImpacts(
  ingredient_id: string
): Promise<{ ingredient_id: string; recipes: IngredientImpactRecipe[]; count: number }> {
  return callFn("bar-admin-list-ingredient-impacts", { ingredient_id });
}

export interface AdminIngredientFull {
  id: string;
  name: string;
  category: string;
  default_unit: string;
  available: boolean;
  is_untracked?: boolean;
  allergen_flags: string[];
  notes: string | null;
}

export function adminCreateIngredient(
  payload: {
    name: string;
    category?: string;
    default_unit?: string;
    available?: boolean;
    is_untracked?: boolean;
    allergen_flags?: string[];
    notes?: string | null;
  }
): Promise<{ ok: true; ingredient: AdminIngredientFull }> {
  return callFn("bar-admin-create-ingredient", payload);
}

export function adminUpdateIngredient(
  payload: {
    ingredient_id: string;
    name?: string;
    category?: string;
    default_unit?: string;
    is_untracked?: boolean;
    allergen_flags?: string[];
    notes?: string | null;
  }
): Promise<{ ok: true; ingredient: AdminIngredientFull }> {
  return callFn("bar-admin-update-ingredient", payload);
}

export interface ActivityTodayRow {
  drink_id: string;
  slug: string;
  name: string;
  service_status: string;
  has_approved: boolean;
  search_count: number;
  load_count: number;
  pin_count: number;
  total_today: number;
  is_featured_today: boolean;
  manual_featured_today_until: string | null;
}

export interface ActivityPopularRow {
  drink_id: string;
  slug: string;
  name: string;
  service_status: string;
  has_approved: boolean;
  search_30: number;
  load_30: number;
  pin_30: number;
  total_30: number;
}

export function adminListActivityToday(): Promise<{
  view: "today";
  service_date: string;
  rows: ActivityTodayRow[];
}> {
  return callFn("bar-admin-list-activity", { view: "today" });
}

export function adminListActivityPopular(): Promise<{
  view: "popular";
  service_date_start: string;
  service_date_end: string;
  rows: ActivityPopularRow[];
}> {
  return callFn("bar-admin-list-activity", { view: "popular" });
}

export function adminResetTodayActivity(
  drink_id: string
): Promise<{ ok: true; drink_id: string; service_date: string; had_row: boolean }> {
  return callFn("bar-admin-reset-today-activity", { drink_id });
}

export type RecipeStandardKind = "glass" | "ice" | "method";

export interface RecipeStandardRow {
  id: string;
  kind: RecipeStandardKind;
  value: string;
  sort_order: number;
}

export function adminListRecipeStandards(): Promise<{
  standards: { glass: RecipeStandardRow[]; ice: RecipeStandardRow[]; method: RecipeStandardRow[] };
}> {
  return callFn("bar-admin-list-recipe-standards", {});
}

export function adminUpdateRecipeStandards(
  kind: RecipeStandardKind,
  values: Array<{ value: string; sort_order?: number }>
): Promise<{ ok: true; kind: RecipeStandardKind; values: RecipeStandardRow[] }> {
  return callFn("bar-admin-update-recipe-standards", { kind, values });
}

// ── Phase 9.1: Issue reports ──

export type IssueType =
  | "Recipe wrong"
  | "Ingredient unavailable"
  | "Wrong ingredient listed"
  | "Build steps unclear"
  | "Glassware/Ice/Method wrong"
  | "Garnish wrong"
  | "Customer feedback"
  | "Other";

export const ISSUE_TYPES: IssueType[] = [
  "Recipe wrong",
  "Ingredient unavailable",
  "Wrong ingredient listed",
  "Build steps unclear",
  "Glassware/Ice/Method wrong",
  "Garnish wrong",
  "Customer feedback",
  "Other",
];

export type IssueStatus = "Open" | "In Review" | "Resolved" | "Dismissed";

export interface IssueRow {
  id: string;
  drink_id: string;
  drink_name: string;
  drink_slug: string | null;
  drink_service_status: string | null;
  recipe_version_id: string | null;
  version_number: string | null;
  version_status: string | null;
  issue_type: IssueType;
  status: IssueStatus;
  note: string;
  station_label: string | null;
  submitted_by: string | null;
  submitted_by_name: string | null;
  resolution_note: string | null;
  resolved_by: string | null;
  resolved_by_name: string | null;
  resolved_at: string | null;
  draft_version_id: string | null;
  notification_sent_at: string | null;
  created_at: string;
  updated_at: string;
}

export function submitIssue(payload: {
  drink_id?: string;
  slug?: string;
  issue_type: IssueType;
  note: string;
  station_label?: string;
}): Promise<{
  ok: true;
  issue: { id: string; status: IssueStatus };
  sms: { targets: number; delivered: number; errors: string[] };
}> {
  return callFn("bar-submit-issue", payload);
}

export function adminListIssues(
  status: IssueStatus | "all" = "all",
  drink_id?: string,
): Promise<{ issues: IssueRow[]; count: number; status_filter: string }> {
  return callFn("bar-admin-list-issues", { status, drink_id });
}

export function adminResolveIssue(
  issue_id: string,
  status: IssueStatus,
  resolution_note?: string | null,
): Promise<{ ok: true; issue: { id: string; status: IssueStatus } }> {
  return callFn("bar-admin-resolve-issue", { issue_id, status, resolution_note });
}

export function adminCreateDraftFromIssue(
  issue_id: string,
): Promise<{
  ok: true;
  already_existed: boolean;
  drink_id: string;
  slug: string | null;
  version_id: string | null;
  version_number: string | null;
  status: string | null;
  change_summary_seed: string;
}> {
  return callFn("bar-admin-create-draft-from-issue", { issue_id });
}

// ── Phase 9.2: Prep recipes ──

export type PrepCategory =
  | "syrup" | "cordial" | "tincture" | "batch"
  | "cream" | "shrub" | "oleo" | "tea" | "juice" | "other";

export const PREP_CATEGORIES: PrepCategory[] = [
  "syrup", "cordial", "tincture", "batch",
  "cream", "shrub", "oleo", "tea", "juice", "other",
];

export type PrepStatus = "Draft" | "Testing" | "Needs Review" | "Approved" | "Retired" | "Archived";

export interface PrepListItem {
  id: string;
  slug: string;
  name: string;
  category: PrepCategory;
  service_status: "Active" | "Hidden" | "Retired";
  active_approved_version_id: string | null;
  approved_version: {
    id: string;
    version_number: string;
    yield_amount: number | null;
    yield_unit: string | null;
    shelf_life_days: number | null;
    storage: string | null;
    ingredients_text: string | null;
    build_instructions: string | null;
    approved_at: string | null;
  } | null;
  version_counts: Record<string, number>;
  updated_at: string;
}

export interface PrepRecipe {
  id: string;
  slug: string;
  name: string;
  category: PrepCategory;
  service_status: "Active" | "Hidden" | "Retired";
  active_approved_version_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface PrepVersion {
  id: string;
  prep_recipe_id: string;
  version_number: string;
  status: PrepStatus;
  yield_amount: number | null;
  yield_unit: string | null;
  shelf_life_days: number | null;
  storage: string | null;
  labeling_instructions: string | null;
  ingredients_text: string | null;
  build_instructions: string | null;
  staff_notes: string | null;
  internal_admin_notes: string | null;
  warning: string | null;
  change_summary: string | null;
  approved_at: string | null;
  approved_by: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface LinkedCocktail {
  drink_id: string;
  drink_slug: string;
  drink_name: string;
  service_status: string;
  version_id: string;
  version_number: string;
  link_via: "ingredient" | "finish_step";
}

export interface PrepDetailResponse {
  prep: PrepRecipe;
  version: PrepVersion | null;
  versions: PrepVersion[];
  linked_cocktails: LinkedCocktail[];
}

export function listPrepRecipes(
  status_filter: "all" | "active_only" | "draft" | "testing" | "approved" | "retired" | "hidden" = "all",
): Promise<{ preps: PrepListItem[]; count: number; filter: string }> {
  return callFn("bar-list-prep-recipes", { status_filter });
}

export function adminGetPrep(args: {
  prep_id?: string;
  slug?: string;
  version_id?: string;
}): Promise<PrepDetailResponse> {
  return callFn("bar-admin-get-prep", args);
}

export function adminCreatePrep(
  name: string,
  category: PrepCategory = "syrup",
): Promise<{
  ok: true;
  prep_id: string;
  slug: string;
  version_id: string;
  version_number: string;
}> {
  return callFn("bar-admin-create-prep", { name, category });
}

export interface UpdatePrepPayload {
  version_id: string;
  prep?: {
    name?: string;
    category?: PrepCategory;
    service_status?: "Active" | "Hidden" | "Retired";
  };
  version?: {
    version_number?: string;
    status?: "Draft" | "Testing" | "Needs Review";
    yield_amount?: number | null;
    yield_unit?: string | null;
    shelf_life_days?: number | null;
    storage?: string | null;
    labeling_instructions?: string | null;
    ingredients_text?: string | null;
    build_instructions?: string | null;
    staff_notes?: string | null;
    internal_admin_notes?: string | null;
    warning?: string | null;
    change_summary?: string | null;
  };
}

export function adminUpdatePrep(
  payload: UpdatePrepPayload,
): Promise<{ ok: true; version_id: string; prep_id: string }> {
  return callFn("bar-admin-update-prep", payload);
}

export function adminApprovePrep(
  version_id: string,
  change_summary: string,
): Promise<{
  ok: true;
  prep_id: string;
  new_approved_id: string;
  previous_approved_id: string | null;
  bumped_to_active: boolean;
}> {
  return callFn("bar-admin-approve-prep", { version_id, change_summary });
}

export function adminDeleteDrink(
  drink_id: string,
): Promise<{ ok: true; drink_id: string; name: string }> {
  return callFn("bar-admin-delete-drink", { drink_id });
}

export function adminDeletePrep(
  prep_id: string,
): Promise<{ ok: true; prep_id: string; name: string }> {
  return callFn("bar-admin-delete-prep", { prep_id });
}

// ── Phase 10.1: app_settings + aliases ──

export interface AppSettings {
  id: number;
  service_day_reset_hour: number;
  max_loaded_drinks: number;
  recently_updated_window_days: number;
  default_station_filter: "today" | "popular" | "all" | "cocktails" | "old_fashioned";
  allow_offline_cache: boolean;
  require_change_summary_on_approval: boolean;
  require_issue_type_on_report: boolean;
  /** Phase 10 polish: kill switch for automatic 86-cascading when an
   * ingredient becomes unavailable. False (default) → cascade-86 checkbox
   * in IngredientImpactModal starts unchecked, future bottle-sync auto
   * cascades disabled. True → cascade-86 starts checked + future auto
   * sync may cascade. Manager always has final say per-toggle. */
  auto_86_on_unavailable_ingredient: boolean;
  issue_notification_email: string | null;
  updated_at: string;
  updated_by: string | null;
}

export function adminGetAppSettings(): Promise<{
  settings: AppSettings | null;
  /** Newest manager-sent mandatory reload (security plan phase 4); see lib/mandatoryReload.ts. */
  last_mandatory_reload?: { id: string; at: string } | null;
  server_now?: string;
}> {
  return callFn("bar-admin-get-app-settings", {});
}

export function adminUpdateAppSettings(
  patch: Partial<Omit<AppSettings, "id" | "updated_at" | "updated_by">>,
): Promise<{ ok: true; settings: AppSettings }> {
  return callFn("bar-admin-update-app-settings", patch);
}

export function adminUpdateAliases(
  drink_id: string,
  aliases: string[],
): Promise<{ ok: true; drink_id: string; aliases: string[]; count: number }> {
  return callFn("bar-admin-update-aliases", { drink_id, aliases });
}

// ── Waitlist host-stand APIs (bar_host or manager/owner) ──

export interface WaitlistEntry {
  source?: "walkin" | "reservation";
  reservation_id?: string | null;
  metadata?: { requested_seating_label?: string | null; requested_table_id?: string | null; [key: string]: unknown } | null;
  id: string;
  /** Contact-free immediate-seat entries have no phone by design. */
  phone: string | null;
  customer_id: string | null;
  first_name_at_intake: string | null;
  party_size: number;
  preferred_room: "any" | "lounge" | "library" | "parlor";
  parlor_holdover_ok: boolean;
  c33_priority: boolean;
  celebration_notes: string | null;
  host_notes: string | null;
  status: "waiting" | "notified" | "final_call" | "seated" | "departed" | "no_show" | "departed_unseated";
  created_at: string;
  notified_at: string | null;
  held_table_id?: string | null;
  hold_expires_at?: string | null;
  arrived_count?: number;
  full_party_arrived_at?: string | null;
  requested_at?: string | null;
  queue_activated_at?: string | null;
  final_call_status?: string | null;
  final_call_error?: string | null;
  notification_status?: string | null;
  notification_error?: string | null;
  final_call_at: string | null;
  seated_at: string | null;
  seated_table_id: string | null;
  seated_room_id: string | null;
  departed_at: string | null;
  business_day: string;
  /** Room the party wants to transfer to once available (null = no pending move). */
  move_to_room: "lounge" | "library" | "parlor" | null;
}

export interface WaitlistTable {
  capacity_allocations?: Array<{
    table_id: string | null;
    group_name: string | null;
    entry_id: string | null;
    reservation_id: string | null;
    party_size: number;
  }>;
  guest_group?: string | null;
  group_capacity?: number;
  id: string;
  room_id: string;
  label: string;
  min_capacity: number;
  max_capacity: number;
  shared_seating: boolean;
  tracks_individual_seats: boolean;
  c33_priority: boolean;
  display_order: number;
  is_active: boolean;
  host_mode?: "exclusive" | "shared" | "none";
  min_party?: number;
}

export function waitlistHostList(): Promise<{
  server_now?: string;
  business_day: string;
  entries: WaitlistEntry[];
  tables: WaitlistTable[];
}> {
  return callFn("waitlist-host-list", {});
}

export function waitlistSetupList(): Promise<{ alerts: import("../types/intercom").TableSetupAlert[] }> {
  return callFn("waitlist-setup", null, { method: "GET" });
}

export function waitlistSetupAction(action: "request" | "server_alerted" | "table_ready" | "dismiss", entry_id: string, table_id?: string | null): Promise<{ alert: import("../types/intercom").TableSetupAlert }> {
  return callFn("waitlist-setup", { action, entry_id, table_id });
}

/* ── Waitlist host settings ── */

export type SmsPhase = "off" | "intake_only" | "full";

export interface WaitlistSettings {
  sms_phase: SmsPhase;
  google_review_url: string;
  review_sms_auto_send_enabled: boolean;
}

export interface HostSettingsPayload {
  settings: WaitlistSettings;
  tables: WaitlistTable[];
}

export function waitlistGetSettings(): Promise<HostSettingsPayload> {
  return callFn("waitlist-host-settings", null, { method: "GET" });
}

export function waitlistSetSmsPhase(phase: SmsPhase): Promise<{ ok: true; sms_phase: SmsPhase }> {
  return callFn("waitlist-host-settings", { action: "set_sms_phase", phase });
}

export function waitlistSetGoogleReviewUrl(url: string): Promise<{ ok: true; google_review_url: string }> {
  return callFn("waitlist-host-settings", { action: "set_google_review_url", url });
}

export function waitlistSetReviewAutoSend(enabled: boolean): Promise<{ ok: true; review_sms_auto_send_enabled: boolean }> {
  return callFn("waitlist-host-settings", { action: "set_review_auto_send", enabled });
}

export function waitlistUpdateTable(
  table_id: string,
  patch: { min_capacity?: number; max_capacity?: number; is_active?: boolean }
): Promise<{ ok: true }> {
  return callFn("waitlist-host-settings", { action: "update_table", table_id, ...patch });
}

// ── Reservations host-stand APIs (bar_host or manager/owner) ──
// Future-dated bookings that materialize into the waitlist ledger on arrival.

export type ReservationStatus =
  | "requested"
  | "confirmed"
  | "reminded"
  | "arrived"
  | "seated"
  | "completed"
  | "canceled"
  | "no_show";

export interface ReservationExperienceRef {
  id: string;
  slug: string;
  title: string;
}

export interface Reservation {
  activation_error?: string | null;
  id: string;
  request_type?: "reservation" | "scheduled_waitlist";
  queue_activated_at?: string | null;
  phone: string;
  customer_id: string | null;
  first_name_at_intake: string | null;
  email: string | null;
  party_size: number;
  preferred_room: "any" | "lounge" | "library" | "parlor";
  c33_priority: boolean;
  guest_tier: string | null;
  reserved_at: string;
  business_day: string;
  duration_minutes: number;
  status: ReservationStatus;
  celebration_notes: string | null;
  host_notes: string | null;
  deposit_model: "none" | "card_hold" | "fixed_deposit" | "prepaid";
  deposit_cents: number;
  payment_state: "none" | "authorized" | "captured" | "voided" | "refunded" | "failed";
  preferred_table_id: string | null;
  preferred_seating_label: string | null;
  waitlist_entry_id: string | null;
  created_at: string;
  confirmed_at: string | null;
  arrived_at: string | null;
  experience: ReservationExperienceRef | null;
}

/** "The book" — upcoming reservations for a business day (defaults to today, 3am rollover). */
export function reservationHostList(
  business_day?: string
): Promise<{ business_day: string; reservations: Reservation[] }> {
  return callFn("reservation-host-list", business_day ? { business_day } : {});
}

/**
 * Cross-day request queue: every un-actioned public booking request
 * (status='requested') from today's business day forward. Future-dated
 * requests never appear in the single-day book, so this is how they get seen.
 */
export function reservationHostListPending(): Promise<{
  business_day: string;
  scope: "pending";
  reservations: Reservation[];
}> {
  return callFn("reservation-host-list", { scope: "pending" });
}

/** requested → confirmed. */
export function reservationConfirm(reservation_id: string): Promise<{ ok: true }> {
  return callFn("reservation-host-api", { action: "confirm", reservation_id });
}

/**
 * Mark a party arrived: creates a linked waitlist_entries row so the guest
 * drops into the live host ledger. Returns the new waitlist_entry_id.
 */
export function reservationArrive(
  reservation_id: string
): Promise<{ ok: true; reservation_id: string; waitlist_entry_id: string | null; already_arrived?: boolean }> {
  return callFn("reservation-host-api", { action: "arrive", reservation_id });
}

/** Pre-arrival → no_show. */
export function reservationNoShow(reservation_id: string): Promise<{ ok: true }> {
  return callFn("reservation-host-api", { action: "no_show", reservation_id });
}

/** Pre-arrival → canceled. */
export function reservationCancel(reservation_id: string): Promise<{ ok: true }> {
  return callFn("reservation-host-api", { action: "cancel", reservation_id });
}

/** Host phone-in booking. Lands as 'confirmed' unless status:'requested' is passed. */
export function reservationAdd(payload: {
  phone: string;
  party_size: number;
  reserved_at: string;
  preferred_room?: "any" | "lounge" | "library" | "parlor";
  first_name_at_intake?: string;
  new_guest_first_name?: string;
  new_guest_last_name?: string;
  email?: string;
  c33_priority?: boolean;
  celebration_notes?: string;
  host_notes?: string;
  experience_slug?: string;
  status?: "requested" | "confirmed";
}): Promise<{ ok: true; reservation_id: string; reservation: Reservation }> {
  return callFn("reservation-host-api", { action: "add", ...payload });
}

/* ── Reservation back-office settings (manager/owner) ── */

export type ReservationSmsPhase = "off" | "confirm_only" | "full";

export interface ReservationServiceHour {
  id: string;
  room_id: string;
  weekday: number; // 0=Sun … 6=Sat
  opens_at: string; // "15:00:00"
  last_seating_at: string;
  slot_interval_min: number;
  is_active: boolean;
}

export interface ReservationExperienceAdmin {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  deposit_model: "none" | "card_hold" | "fixed_deposit" | "prepaid";
  party_size_min: number;
  party_size_max: number;
  turn_time_minutes: number;
  is_active: boolean;
  c33_only: boolean;
  display_order: number;
  policy_text: string | null;
}

export interface ReservationSettingsPayload {
  settings: {
    reservation_sms_phase?: ReservationSmsPhase;
    min_lead_minutes?: number;
    min_lead_days?: number;
    booking_horizon_days?: number;
    no_show_grace_minutes?: number;
    booking_ceiling?: number;
    [key: string]: unknown;
  };
  service_hours: ReservationServiceHour[];
  experiences: ReservationExperienceAdmin[];
  cancellation_policies: {
    id: string; name: string; free_cancel_hours: number;
    no_show_charge_cents: number | null; charge_on_late_cancel: boolean;
  }[];
  rooms: { id: string; display_name: string; display_order: number }[];
}

export function reservationSettingsGet(): Promise<ReservationSettingsPayload> {
  return callFn("reservation-settings", null, { method: "GET" });
}

export function reservationSetSetting(key: string, value: unknown): Promise<{ ok: true }> {
  return callFn("reservation-settings", { action: "set_setting", key, value });
}

export function reservationUpsertHours(row: {
  room_id: string; weekday: number; opens_at: string; last_seating_at: string; slot_interval_min?: number;
}): Promise<{ ok: true }> {
  return callFn("reservation-settings", { action: "upsert_hours", ...row });
}

export function reservationDeleteHours(room_id: string, weekday: number): Promise<{ ok: true }> {
  return callFn("reservation-settings", { action: "delete_hours", room_id, weekday });
}

export function reservationUpdateExperience(
  id: string,
  patch: Partial<Pick<ReservationExperienceAdmin, "title" | "description" | "policy_text" | "party_size_min" | "party_size_max" | "is_active">>,
): Promise<{ ok: true }> {
  return callFn("reservation-settings", { action: "update_experience", id, patch });
}

export interface AdminDashboardStatsResponse {
  sales: any[];
  cogs: any[];
  category_mix: any[];
  payments: any[];
  labor: any[];
}

export async function adminGetDashboardStats(
  p_start: string,
  p_end: string
): Promise<AdminDashboardStatsResponse> {
  const [salesRes, cogsRes, catRes, payRes, laborRes] = await Promise.allSettled([
    callRpc<any[]>("get_dashboard_sales_daily", { p_start, p_end }),
    callRpc<any[]>("get_dashboard_cogs_daily", { p_start, p_end }),
    callRpc<any[]>("get_dashboard_category_mix", { p_start, p_end }),
    callRpc<any[]>("get_dashboard_payments", { p_start, p_end }),
    callRpc<any[]>("get_dashboard_labor", { p_start, p_end }),
  ]);

  const errors: string[] = [];
  if (salesRes.status === "rejected") errors.push(salesRes.reason?.message || "Sales error");
  if (cogsRes.status === "rejected") errors.push(cogsRes.reason?.message || "COGS error");
  if (catRes.status === "rejected") errors.push(catRes.reason?.message || "Category mix error");
  if (payRes.status === "rejected") errors.push(payRes.reason?.message || "Payments error");

  if (salesRes.status === "rejected" && cogsRes.status === "rejected" && catRes.status === "rejected" && payRes.status === "rejected") {
    throw new Error(errors[0] || "Failed to load dashboard data");
  }

  return {
    sales: salesRes.status === "fulfilled" ? salesRes.value : [],
    cogs: cogsRes.status === "fulfilled" ? cogsRes.value : [],
    category_mix: catRes.status === "fulfilled" ? catRes.value : [],
    payments: payRes.status === "fulfilled" ? payRes.value : [],
    labor: laborRes.status === "fulfilled" ? laborRes.value : [],
  };
}

export interface AdminTipsComparisonResponse {
  payments: any[];
  category_mix: any[];
}

/**
 * Lightweight fetch for the tip-trend comparison window: only the two RPCs
 * the tip-rate math needs. Never throws — the comparison is an enhancement,
 * and a failed window just renders the tile without a delta.
 */
export async function adminGetTipsComparison(
  p_start: string,
  p_end: string
): Promise<AdminTipsComparisonResponse> {
  const [payRes, catRes] = await Promise.allSettled([
    callRpc<any[]>("get_dashboard_payments", { p_start, p_end }),
    callRpc<any[]>("get_dashboard_category_mix", { p_start, p_end }),
  ]);
  return {
    payments: payRes.status === "fulfilled" ? payRes.value : [],
    category_mix: catRes.status === "fulfilled" ? catRes.value : [],
  };
}

export interface PresenceItem {
  customer_id: string;
  label: string;
  active_drink_slug: string | null;
  active_drink_name: string | null;
  active_drink_count: number | null;
  updated_at: string;
}

export interface PresencePingResponse {
  ok: boolean;
  presence: PresenceItem[];
}

export function pingPresence(
  active_drink_slug: string | null,
  active_drink_name: string | null,
  active_drink_count: number | null
): Promise<PresencePingResponse> {
  return callFn<PresencePingResponse>("bar-presence-ping", {
    active_drink_slug,
    active_drink_name,
    active_drink_count,
  });
}

// ── Comp Audit & Discount Review APIs ──

export interface CompAuditLineItem {
  name?: string;
  is_bottle?: boolean;
  applied_cents?: number;
}

export interface CompAuditRow {
  id: string;
  square_order_id: string;
  discount_uid: string;
  discount_name: string | null;
  square_discount_catalog_id: string | null;
  applied_cents: number;
  order_closed_at: string | null;
  severity: "bottle_alert" | "review";
  status: CompAuditStatus;
  source: "webhook" | "backfill";
  line_items: CompAuditLineItem[];
  digest_sent_at: string | null;
  alerted_at: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  // Guest + staff, joined from square_payments at read time (Library migration
  // 20261004000001). Optional: absent until that migration is applied.
  payment_synced?: boolean;          // false = sync-payments hasn't caught this order yet
  customer_name?: string | null;     // linked profile; null = walk-in or unlinked Square guest
  has_square_customer?: boolean;     // true + null customer_name = Square guest, no local profile
  staff_names?: string[];            // who took payment (Square doesn't record who applied a discount)
  tender_summary?: string | null;    // e.g. "VISA ••1234, CASH"
  // Guest's C33 membership state AT THE TIME the comp was recorded (Library
  // migration 20261004200000). null/absent = not recorded (older rows).
  c33_status?: CompAuditC33Status | null;
  c33_membership_id?: string | null;
  c33_billing_health?: string | null;
  // A customers row that is the guest on the order is also a staff member who took
  // payment on it, matched by account id (Library migration 20261004230000).
  guest_is_staff?: boolean;
}

export type CompAuditC33Status =
  | "active" | "canceled" | "lapsed" | "expired" | "purchased"
  | "non_member" | "unlinked_guest" | "no_guest";

// auto_reviewed = a C33 comp the system applied itself; logged, never queued.
export type CompAuditStatus = "pending" | "reviewed" | "dismissed" | "auto_reviewed";

export interface CompSanctionedDiscount {
  id: string;
  match_type: "catalog_id" | "name_pattern";
  match_value: string;
  category_scoped: boolean;
  active: boolean;
  note: string | null;
  created_at: string;
}

export function getCompAuditQueue(
  status: CompAuditStatus | null = "pending",
  limit = 100,
  offset = 0
): Promise<CompAuditRow[]> {
  return callRpc<CompAuditRow[]>("get_comp_audit_queue", {
    p_status: status,
    p_limit: limit,
    p_offset: offset,
  });
}

export function setCompAuditStatus(
  id: string,
  status: "pending" | "reviewed" | "dismissed"
): Promise<{ ok: boolean; error?: string }> {
  return callRpc<{ ok: boolean; error?: string }>("comp_audit_set_status", {
    p_id: id,
    p_status: status,
  });
}

export function getCompSanctionedDiscounts(): Promise<CompSanctionedDiscount[]> {
  return callRpc<CompSanctionedDiscount[]>("get_comp_sanctioned_discounts", {});
}

// ── C33 Welcome Kits queue (migration 20260831000002_gift_kit_buyer_pickup) ──
// One row per membership that has welcome-kit rows; `items` is the per-item
// ledger. Keyed off gift_item_fulfillment rows, never the rollup column.

export interface WelcomeKitItem {
  id: string;
  item_name: string;
  quantity: number;
  status: "pending" | "fulfilled" | "canceled";
  picked_up_by: string | null;
  picked_up_at: string | null;
  square_order_id: string | null;
}

export interface WelcomeKitRow {
  membership_id: string;
  member_number: number | null;
  membership_status: string;
  membership_type: string;
  gift_items_status: string | null;
  /** Gift buyer is taking the kit at purchase to present in person. */
  buyer_pickup: boolean;
  buyer_pickup_set_at: string | null;
  recipient_customer_id: string;
  recipient_name: string;
  /** null for self-signups and legacy gifts with no recorded purchaser. */
  purchaser_customer_id: string | null;
  purchaser_name: string | null;
  gift_delivery_date: string | null;
  gift_delivery_time: string | null;
  joined_at: string;
  activated_at: string | null;
  picked_up_at: string | null;
  pending_count: number;
  oldest_pending_at: string | null;
  items: WelcomeKitItem[];
}

export function getWelcomeKitQueue(
  status: "pending" | "released" | null = "pending",
  limit = 200
): Promise<WelcomeKitRow[]> {
  return callRpc<WelcomeKitRow[]>("get_welcome_kit_queue", {
    p_status: status,
    p_limit: limit,
  });
}

export interface SetKitBuyerPickupResult {
  ok: boolean;
  error?: string;
  buyer_pickup?: boolean;
  pending_count?: number;
  gift_items_status?: string | null;
  picked_up_at?: string | null;
}

export function setKitBuyerPickup(
  membershipId: string,
  enabled: boolean
): Promise<SetKitBuyerPickupResult> {
  return callRpc<SetKitBuyerPickupResult>("set_gift_kit_buyer_pickup", {
    p_membership_id: membershipId,
    p_enabled: enabled,
  });
}

export interface ReleaseWelcomeKitResult {
  ok: boolean;
  error?: string;
  released_count?: number;
  remaining_pending?: number;
  released_to?: "purchaser" | "member";
}

/** Manual "Mark released". itemIds null = every pending item; a subset is a
 *  partial handover (an item out of stock). The RPC derives the staff name
 *  from the session when staffName is omitted. releasedTo is explicit
 *  attribution — the RPC only infers from the buyer-pickup flag when this is
 *  null/omitted. The `(purchaser)` stamp it produces is what the member's
 *  /my page keys on to say "handed to the person who gifted your membership",
 *  so staff must state the target rather than let it be guessed. */
export function releaseWelcomeKit(
  membershipId: string,
  opts: {
    itemIds?: string[] | null;
    squareOrderId?: string | null;
    staffName?: string | null;
    releasedTo?: "purchaser" | "member";
  } = {}
): Promise<ReleaseWelcomeKitResult> {
  return callRpc<ReleaseWelcomeKitResult>("release_welcome_kit", {
    p_membership_id: membershipId,
    p_staff_name: opts.staffName ?? null,
    p_square_order_id: opts.squareOrderId ?? null,
    p_item_ids: opts.itemIds ?? null,
    p_released_to: opts.releasedTo ?? null,
  });
}

// ── C33 Member Operating View & Membership Management APIs ──

export interface MemberOperatingViewRow {
  member_id: string;
  customer_id: string;
  member_number: number | null;
  name: string;
  joined_at: string | null;
  term_end_date: string | null;
  days_until_renewal: number | null;
  visit_count: number;
  last_visit: string | null;
  days_since_visit: number;
  total_spend_cents: number;
  pours_used: number;
  pours_allowed: number;
  risk_score: number;
  risk_label: "Healthy" | "Watch" | "At Risk";
  next_best_action: string;
  membership_type: "standard" | "gift" | "ambassador";
  phone: string | null;
  email: string | null;
  lifecycle_status: string | null;
  date_of_birth: string | null;
  billing_status:
    | "paid"
    | "renewal_soon"
    | "invoiced"
    | "not_paid"
    | "needs_review"
    | "gift_pending"
    | "gift_overdue"
    | "declined"
    | "lapsed";
  auto_renew: boolean;
}

export function getMemberOperatingView(
  p_view: string = "all"
): Promise<MemberOperatingViewRow[]> {
  return callRpc<MemberOperatingViewRow[]>("get_member_operating_view", {
    p_view,
  });
}

export interface MemberDetailResponse {
  ok: boolean;
  customer?: any;
  membership?: any;
  orders?: any[];
  tastings?: any[];
  benefits?: any[];
  redemptions?: any[];
  gift?: any;
  notes?: any[];
  error?: string;
}

export function getMemberDetail(
  customerId: string,
  membershipId?: string
): Promise<MemberDetailResponse> {
  return callFn<MemberDetailResponse>("manage-membership", {
    action: "get-detail",
    customer_id: customerId,
    membership_id: membershipId,
  });
}

export function updateMemberStatus(
  membershipId: string,
  updates: {
    status?: string;
    term_start_date?: string;
    term_end_date?: string;
    auto_renew?: boolean;
  }
): Promise<{ ok: boolean; error?: string }> {
  return callFn<{ ok: boolean; error?: string }>("manage-membership", {
    action: "update-membership",
    membership_id: membershipId,
    ...updates,
  });
}

// ── Club Pour Planner APIs ──

export interface ClubPourFeature {
  id: string;
  month_id: string;
  menu_item_id: string;
  bottle_name_snapshot: string | null;
  feature_notes: string | null;
  display_order: number;
  source: "square" | "manual";
  menu_items?: {
    id: string;
    name: string;
  };
}

export interface ClubPourMonth {
  id: string;
  month_year: string;
  title: string | null;
  description: string | null;
  is_active: boolean;
  collective_33_features?: ClubPourFeature[];
}

export interface ClubPoursListResponse {
  ok: boolean;
  current_month: string;
  months: ClubPourMonth[];
  current_featured: { id: string; name: string }[];
}

export interface SearchBottleItem {
  id: string;
  name: string;
  is_club_featured: boolean;
}

export function listClubPours(): Promise<ClubPoursListResponse> {
  return callFn<ClubPoursListResponse>("club-pours-admin", { action: "list" });
}

export function snapshotClubPoursNow(): Promise<{ ok: boolean; result: any }> {
  return callFn<{ ok: boolean; result: any }>("club-pours-admin", {
    action: "snapshot_now",
  });
}

export function upsertClubPourMonth(payload: {
  month_year: string;
  title?: string | null;
  description?: string | null;
}): Promise<{ ok: boolean; month: ClubPourMonth }> {
  return callFn<{ ok: boolean; month: ClubPourMonth }>("club-pours-admin", {
    action: "upsert_month",
    ...payload,
  });
}

export function deleteClubPourMonth(
  month_year: string
): Promise<{ ok: boolean }> {
  return callFn<{ ok: boolean }>("club-pours-admin", {
    action: "delete_month",
    month_year,
  });
}

export function searchClubPourBottles(
  q: string
): Promise<{ ok: boolean; bottles: SearchBottleItem[] }> {
  return callFn<{ ok: boolean; bottles: SearchBottleItem[] }>(
    "club-pours-admin",
    { action: "search_bottles", q }
  );
}

export function saveClubPourLineup(payload: {
  month_year: string;
  items: {
    menu_item_id: string;
    feature_notes?: string | null;
    display_order: number;
  }[];
}): Promise<{ ok: boolean; saved: number }> {
  return callFn<{ ok: boolean; saved: number }>("club-pours-admin", {
    action: "save_lineup",
    ...payload,
  });
}

// ── Cash Management & Drawer Count APIs ──

export interface CashDrawerSummary {
  business_date: string;
  total_cash_sales_cents: number;
  total_tips_cents: number;
  total_paid_in_cents: number;
  total_paid_out_cents: number;
  expected_closing_cents: number;
  total_closing_counted_cents: number;
  total_deposit_cents: number;
  total_variance_cents: number;
  has_discrepancy: boolean;
}

export interface CashDrawerShift {
  id: string;
  device_id: string;
  device_name: string;
  // ENDED: ended in Square but the drawer was never closed there.
  state: "OPEN" | "ENDED" | "CLOSED";
  opened_at: string;
  closed_at: string | null;
  opened_cash_cents: number;
  closed_cash_cents: number | null;
  // Column names as stored in public.cash_drawer_shifts.
  cash_payment_cents: number | null;
  cash_refund_cents: number | null;
  cash_paid_in_cents: number;
  cash_paid_out_cents: number;
  expected_cash_cents: number | null;
  // Manager/owner deposit-bag verification (verify_drawer_deposit).
  staff_counted_cents?: number | null;
  staff_counted_by_name?: string | null;
  staff_counted_at?: string | null;
  staff_count_notes?: string | null;
  /** Set client-side by splitHeldOverCash: cash taken on later days on a drawer left open. */
  held_over_cents?: number | null;
  /** Square team members who opened / closed the drawer (from Square's drawer record). */
  opening_staff_name?: string | null;
  closing_staff_name?: string | null;
}

export interface CashDrawerBank {
  device_id: string;
  device_name: string;
  standard_bank_cents: number;
  bank_breakdown: Record<string, number> | null;
  low_thresholds: Record<string, number> | null;
}

export interface CashDrawerCountRow {
  id: string;
  shift_id: string;
  device_id: string;
  device_name: string;
  count_type: "open" | "close" | "opening" | "closing";
  total_cents: number;
  denomination_breakdown: Record<string, number>;
  retained_breakdown?: Record<string, number> | null;
  retained_cents?: number | null;
  deposit_breakdown?: Record<string, number> | null;
  deposit_cents?: number | null;
  bank_shortfall_cents?: number | null;
  deposit_bag_number?: string | null;
  deposit_confirmed_at?: string | null;
  created_at: string;
  counted_by_name?: string;
  has_discrepancy?: boolean;
  /** Stored at submit; null when the count came before the Square close. */
  square_amount_cents?: number | null;
  square_expected_cents?: number | null;
  /** Read live from the day's last Square shift by get_cash_drawer_daily. */
  square_amount_live_cents?: number | null;
  square_expected_live_cents?: number | null;
}

export interface CashDrawerDailyResponse {
  summary: CashDrawerSummary;
  shifts: CashDrawerShift[];
  banks: CashDrawerBank[];
  counts: CashDrawerCountRow[];
  events: any[];
}

// Shape returned by get_pending_bank_deposit (Library 20260803000001 §6):
// fully verified days not yet swept by a bank run, plus any stale runs.
export interface PendingBankDepositResponse {
  total_cents: number;
  days: PendingDay[];
  stale_runs: Array<{ id: string; verified_at: string; counted_cents: number; stale_reason: string | null }>;
}

export interface DepositBankRun {
  id: string;
  verified_at: string;
  verified_by_name: string | null;
  period_start: string | null;
  period_end: string | null;
  expected_cents: number;
  counted_cents: number;
  variance_cents: number;
  included_dates: string[] | null;
  status: "verified" | "stale";
  stale_reason: string | null;
  notes: string | null;
}

export interface CashDrawerMonthlyDay {
  business_date: string;
  cash_sales_cents: number;
  paid_in_cents: number;
  paid_out_cents: number;
  deposit_cents: number;
  variance_cents: number;
  has_unverified_bags: boolean;
  status: string;
  /** Square drawers closed that business day; 0 means there are no bags to verify. */
  drawers_closed: number;
  /** Swept into a bank run (deposit_bank_runs). */
  banked: boolean;
  /** Square drawers that day, open or closed; more than drawers_closed means one is still open. */
  total_drawers: number;
  /** The run's status: "stale" once an owner re-opened a bag on a covered day. */
  bank_run_status: "verified" | "stale" | null;
}

export interface CashDrawerMonthlyResponse {
  year: number;
  month: number;
  summary: {
    total_sales_cents: number;
    total_deposits_cents: number;
    total_variance_cents: number;
    days_count: number;
  };
  days: CashDrawerMonthlyDay[];
}

export function getCashDrawerDaily(
  dateStr: string
): Promise<CashDrawerDailyResponse> {
  return callRpc<CashDrawerDailyResponse>("get_cash_drawer_daily", {
    p_date: dateStr,
  });
}

/** Throws on { ok:false } (non-managers get "Manager role required"); callers hide the panel. */
export async function getPendingBankDeposit(): Promise<PendingBankDepositResponse> {
  const res = await callCashRpc<any>("get_pending_bank_deposit", {});
  return {
    total_cents: Number(res.total_cents ?? 0),
    days: Array.isArray(res.days) ? res.days : [],
    stale_runs: Array.isArray(res.stale_runs) ? res.stale_runs : [],
  };
}

export async function getDepositBankRuns(limit = 24): Promise<DepositBankRun[]> {
  const res = await callRpc<any>("get_deposit_bank_runs", { p_limit: limit });
  return Array.isArray(res) ? res : [];
}

export async function getCashDrawerMonthly(
  year: number,
  month: number
): Promise<CashDrawerMonthlyResponse> {
  const raw = await callRpc<any>("get_cash_drawer_monthly", {
    p_year: year,
    p_month: month,
  });

  const rows: any[] = Array.isArray(raw) ? raw : (raw?.days || []);

  let totalSales = 0;
  let totalDeposits = 0;
  let totalVariance = 0;

  const days: CashDrawerMonthlyDay[] = rows.map((r: any) => {
    const sales = Number(r.total_cash_sales_cents ?? r.cash_sales_cents ?? 0);
    const expectedDep = Number(r.expected_deposit_cents ?? r.deposit_cents ?? 0);
    const actualDep = r.actual_deposit_cents != null ? Number(r.actual_deposit_cents) : expectedDep;
    const variance = Number(r.total_variance_cents ?? r.variance_cents ?? 0);

    totalSales += sales;
    totalDeposits += (r.actual_deposit_cents != null ? actualDep : expectedDep);
    totalVariance += variance;

    return {
      business_date: String(r.business_date).slice(0, 10),
      cash_sales_cents: sales,
      paid_in_cents: Number(r.paid_in_cents || 0),
      paid_out_cents: Number(r.paid_out_cents || 0),
      deposit_cents: r.actual_deposit_cents ?? expectedDep,
      variance_cents: variance,
      has_unverified_bags: !r.verified_at,
      status: r.verified_at ? "verified" : "pending",
      drawers_closed: Number(r.drawers_closed ?? 0),
      total_drawers: Number(r.total_drawers ?? r.drawers_closed ?? 0),
      banked: r.bank_run_id != null,
      bank_run_status: r.bank_run_status ?? null,
    };
  });

  return {
    year,
    month,
    summary: {
      total_sales_cents: totalSales,
      total_deposits_cents: totalDeposits,
      total_variance_cents: totalVariance,
      days_count: days.length,
    },
    days,
  };
}

export function submitCashDrawerCount(payload: {
  action?: "submit" | "count";
  shift_id?: string;
  device_id: string;
  device_name?: string;
  count_type: "open" | "close" | "opening" | "closing";
  total_cents?: number;
  denomination_breakdown?: Record<string, number>;
  denominations?: Record<string, number>;
  notes?: string;
  retained_breakdown?: Record<string, number>;
  retained_cents?: number;
  deposit_breakdown?: Record<string, number>;
  deposit_cents?: number;
  bank_shortfall_cents?: number;
  deposit_bag_number?: string;
  deposit_confirmed?: boolean;
  deposit_confirmed_at?: string;
}): Promise<{ ok: boolean; count?: CashDrawerCountRow; reveal?: any; error?: string }> {
  const normCountType = (payload.count_type === "open" || payload.count_type === "opening") ? "opening" : "closing";
  const denoms = payload.denominations || payload.denomination_breakdown || {};
  return callFn<{ ok: boolean; count?: CashDrawerCountRow; reveal?: any; error?: string }>(
    "cash-drawer-count",
    {
      action: "submit",
      device_id: payload.device_id,
      device_name: payload.device_name,
      count_type: normCountType,
      denominations: denoms,
      notes: payload.notes,
      retained_breakdown: payload.retained_breakdown,
      deposit_breakdown: payload.deposit_breakdown,
      bank_shortfall_cents: payload.bank_shortfall_cents,
      deposit_bag_number: payload.deposit_bag_number,
      deposit_confirmed: payload.deposit_confirmed ?? (payload.count_type === "close" || payload.count_type === "closing" ? true : undefined),
    }
  );
}

// Deposit-bag verification is a Postgres RPC (Library migrations
// 20260803000001 / 20260803000002), not an action of the cash-drawer-count
// edge function. A refusal comes back as HTTP 200 { ok:false, error }, so it
// is thrown here rather than mistaken for success.
async function callCashRpc<T>(rpcName: string, body: object): Promise<T> {
  const res = await callRpc<any>(rpcName, body);
  if (!res || res.ok === false) {
    throw new ApiError(res?.error || `${rpcName} failed`, 400);
  }
  return res as T;
}

export function verifyDrawerDeposit(payload: {
  shift_id: string;
  counted_cents: number;
  denominations?: Record<string, number>;
  notes?: string;
}): Promise<{ ok: true; counted_by: string; day_complete: boolean; day_total_cents: number }> {
  return callCashRpc("verify_drawer_deposit", {
    p_shift_id: payload.shift_id,
    p_counted_cents: payload.counted_cents,
    p_denominations: payload.denominations ?? null,
    p_notes: payload.notes ?? null,
  });
}

/** verify_bank_run: sweeps the verified, unbanked days (p_dates, or all when null) into one run. */
export function verifyBankRun(payload: {
  counted_cents: number;
  notes?: string;
  dates: string[];
}): Promise<{ ok: true; run: DepositBankRun }> {
  return callCashRpc("verify_bank_run", {
    p_counted_cents: payload.counted_cents,
    p_notes: payload.notes ?? null,
    p_dates: payload.dates,
  });
}

export function reopenDrawerDeposit(payload: {
  shift_id: string;
  reason: string;
}): Promise<{ ok: true; business_date: string; staled_run_id: string | null }> {
  return callCashRpc("reopen_drawer_deposit", {
    p_shift_id: payload.shift_id,
    p_reason: payload.reason,
  });
}

// ── Shift Operations & Checklists APIs ──

export interface ShiftLogData {
  id: string;
  shift_type: "opening" | "mid" | "closing";
  opened_by?: string;
  opened_at?: string;
  closed_at?: string | null;
  status: "open" | "closed";
  customers?: { display_name?: string };
}

export interface ShiftLogEntryRow {
  id: string;
  shift_id: string;
  entry_type: "note" | "incident" | "equipment_issue" | "inventory_shortage" | "guest_complaint" | "guest_compliment";
  content: string;
  logged_by_name?: string;
  created_at: string;
}

export interface DailyChecklistItem {
  id: string;
  title: string;
  category: "opening" | "closing";
  completed: boolean;
  completed_by?: string | null;
  completed_at?: string | null;
}

export function getActiveShift(): Promise<{ ok: boolean; shift: ShiftLogData | null; entries?: ShiftLogEntryRow[] }> {
  return callFn("shift-log", { action: "get_active" });
}

export function addShiftEntry(payload: {
  shift_id?: string;
  entry_type: string;
  content: string;
}): Promise<{ ok: boolean; entry: ShiftLogEntryRow }> {
  return callFn("shift-log", { action: "add_entry", ...payload });
}

export function getDailyChecklist(): Promise<{ ok: boolean; items: DailyChecklistItem[] }> {
  return callFn("daily-checklist", { action: "get_checklist" });
}

export function toggleDailyChecklistItem(payload: {
  item_id: string;
  completed: boolean;
}): Promise<{ ok: boolean }> {
  return callFn("daily-checklist", { action: "toggle_item", ...payload });
}

// ── Merge Duplicates APIs (Bottles, Distilleries, Customers) ──

export interface DuplicatePair {
  item_a: any;
  item_b: any;
  confidence?: "high" | "medium" | "low";
  match_type?: string;
  reason?: string;
}

export interface MergeCompareResult {
  item_a: any;
  item_b: any;
  conflicts?: any[];
}

export interface MergeHistoryEntry {
  id: string;
  primary_id: string;
  duplicate_id?: string;
  secondary_id?: string;
  primary_name?: string;
  duplicate_name?: string;
  merged_by?: string;
  merged_at?: string;
  created_at?: string;
  details?: any;
}

export function findDuplicates(
  entityType: "bottles" | "distilleries" | "customers"
): Promise<{ ok?: boolean; pairs: DuplicatePair[] }> {
  if (entityType === "customers") {
    return callFn("merge-customers", { action: "find-duplicates" });
  }
  if (entityType === "distilleries") {
    return callFn("enrich-distilleries", { action: "find-distillery-duplicates" });
  }
  return callFn("merge-items", { action: "find-duplicates" });
}

export function searchMergeEntities(
  entityType: "bottles" | "distilleries" | "customers",
  query: string
): Promise<{ items?: any[]; distilleries?: any[]; customers?: any[] }> {
  // merge-customers action names must match the edge function's switch exactly
  // (supabase/functions/merge-customers/index.ts in the Library repo). They never did
  // before 2026-09-14 — search/compare/merge/history all returned "Unknown action".
  if (entityType === "customers") {
    return callFn("merge-customers", { action: "search-customers", query });
  }
  if (entityType === "distilleries") {
    return callFn("enrich-distilleries", { action: "search-distilleries", query });
  }
  return callFn("merge-items", { action: "search", query });
}

export function compareMergeEntities(
  entityType: "bottles" | "distilleries" | "customers",
  idA: string,
  idB: string
): Promise<MergeCompareResult> {
  if (entityType === "customers") {
    return callFn("merge-customers", { action: "compare-customers", id_a: idA, id_b: idB });
  }
  if (entityType === "distilleries") {
    return callFn("enrich-distilleries", {
      action: "compare-distilleries",
      primary_id: idA,
      secondary_id: idB,
    });
  }
  return callFn("merge-items", {
    action: "compare",
    item_a_id: idA,
    item_b_id: idB,
  });
}

export function executeMerge(
  entityType: "bottles" | "distilleries" | "customers",
  payload: {
    primary_id: string;
    secondary_id: string;
    field_overrides?: Record<string, any>;
  }
): Promise<{ ok: boolean; message?: string }> {
  if (entityType === "customers") {
    // execute-merge returns { success, ... } on 200 and a non-2xx { error } otherwise
    // (callFn throws on the latter), so a resolved promise means the merge completed.
    return callFn<{ success?: boolean; error?: string }>("merge-customers", {
      action: "execute-merge",
      primary_id: payload.primary_id,
      secondary_id: payload.secondary_id,
      field_overrides: payload.field_overrides,
    }).then((r) => {
      if (!r?.success) throw new ApiError(r?.error || "merge-customers did not confirm the merge", 500);
      return { ok: true };
    });
  }
  if (entityType === "distilleries") {
    return callFn("enrich-distilleries", {
      action: "merge-distilleries",
      primary_id: payload.primary_id,
      secondary_id: payload.secondary_id,
      field_overrides: payload.field_overrides,
    });
  }
  return callFn("merge-items", {
    action: "merge",
    primary_id: payload.primary_id,
    duplicate_id: payload.secondary_id,
    field_overrides: payload.field_overrides,
  });
}

export function getMergeHistoryList(
  entityType: "bottles" | "distilleries" | "customers"
): Promise<{ ok?: boolean; history: MergeHistoryEntry[] }> {
  if (entityType === "customers") {
    // merge-history returns tombstoned rows + the rows they merged into; shape it for the UI.
    type Person = { id: string; first_name?: string | null; last_name?: string | null; email?: string | null; phone?: string | null };
    return callFn<{ items?: (Person & { merged_into_id: string; merged_at?: string })[]; primaries?: Person[] }>(
      "merge-customers",
      { action: "merge-history" }
    ).then((r) => {
      const label = (p?: Person) =>
        p ? [p.first_name, p.last_name].filter(Boolean).join(" ") || p.email || p.phone || p.id : undefined;
      const primaries = new Map((r.primaries || []).map((p) => [p.id, p]));
      return {
        ok: true,
        history: (r.items || []).map((t) => ({
          id: t.id,
          primary_id: t.merged_into_id,
          secondary_id: t.id,
          primary_name: label(primaries.get(t.merged_into_id)),
          duplicate_name: label(t),
          merged_at: t.merged_at,
        })),
      };
    });
  }
  if (entityType === "distilleries") {
    return callFn("enrich-distilleries", { action: "distillery-merge-history" });
  }
  return callFn("merge-items", { action: "history" });
}

// ── Flight Builder & Manager APIs ──

export interface FlightPour {
  id?: string;
  menu_item_id: string;
  pour_order: number;
  pour_size_oz: number;
  pour_name: string;
  square_pour_item_id?: string | null;
  bottle_id?: string | null;
  bottle_name?: string | null;
  pours_remaining?: number | null;
  status?: string;
  note?: string | null;
}

export interface FlightItem {
  id: string;
  name: string;
  status: string;
  pour_price_cents?: number | null;
  square_pour_variation_id?: string | null;
  description?: string | null;
  pours: FlightPour[];
}

export interface SearchPourItem {
  id: string;
  name: string;
  square_pour_item_id?: string | null;
  bottle_id?: string | null;
  bottle_name?: string | null;
  pours_remaining?: number | null;
  status?: string;
}

export interface FlightAiSuggestion {
  theme: string;
  name_suggestion?: string;
  description: string;
  pours: {
    menu_item_id: string;
    name: string;
    bottle_id?: string;
    bottle_name?: string;
    pour_order: number;
    pour_size_oz: number;
    note?: string;
  }[];
  total_pour_price_cents?: number;
  dropped?: number;
  catalog_size?: number;
}

export function listAdminFlights(): Promise<{ ok?: boolean; flights: FlightItem[] }> {
  return callFn("manage-flight-pours", { action: "list_flights" });
}

export function searchAdminPours(
  q: string,
  limit = 20
): Promise<{ ok?: boolean; pours: SearchPourItem[] }> {
  return callFn("manage-flight-pours", { action: "list_pours", q, limit });
}

export function findPourByUpc(code: string): Promise<{
  found: boolean;
  reason?: string;
  matched_upc?: string;
  bottle?: any;
  pour?: SearchPourItem;
}> {
  return callFn("manage-flight-pours", { action: "find_pour_by_upc", code });
}

export function setFlightPours(payload: {
  flight_id: string;
  pours: {
    menu_item_id: string;
    pour_order: number;
    pour_size_oz: number;
  }[];
}): Promise<{ ok: boolean; count: number }> {
  return callFn("manage-flight-pours", {
    action: "set_flight_pours",
    ...payload,
  });
}

export function generateFlightAi(payload: {
  flight_id?: string;
  guidance?: string;
  size?: number;
  max_price_cents?: number;
}): Promise<FlightAiSuggestion> {
  return callFn("generate-flight", {
    action: "suggest",
    ...payload,
  });
}

export function saveFlightDescription(payload: {
  flight_id: string;
  description: string;
}): Promise<{ ok: boolean }> {
  return callFn("generate-flight", {
    action: "save_description",
    ...payload,
  });
}

// ── Catalog Sync & Deleted Items Monitor APIs ──

export interface CatalogChangeStats {
  window_days: number;
  in_progress: boolean;
  last_run: {
    status: string;
    started_at: string;
    completed_at?: string;
    items_created?: number;
    items_updated?: number;
    items_skipped?: number;
    deactivated?: number;
  } | null;
  totals: {
    created: number;
    updated: number;
    deactivated: number;
    total: number;
  };
  price_changes: number;
  by_field: Array<{ field_name: string; change_count: number }>;
}

export interface CatalogChangeRow {
  id: string;
  recorded_at: string;
  change_type: "created" | "updated" | "deactivated";
  field_name: string | null;
  old_value: any;
  new_value: any;
  menu_item_id: string | null;
  item_name: string | null;
  category_slug: string | null;
  square_variation_id: string | null;
}

export interface DeletedItemRow {
  id?: string;
  name: string;
  square_id: string;
  ref_type: "bottle" | "pour" | "unknown";
  is_active: boolean;
  last_synced_at?: string | null;
  audit_date?: string | null;
  is_reviewed?: boolean;
}

export function getCatalogChangeStats(days = 30): Promise<CatalogChangeStats> {
  return callRpc<CatalogChangeStats>("get_catalog_change_stats", { p_days: days });
}

export function getCatalogChanges(params: {
  days?: number;
  change_type?: string;
  field?: string;
  limit?: number;
}): Promise<CatalogChangeRow[]> {
  return callRpc<CatalogChangeRow[]>("get_catalog_changes", {
    p_days: params.days || 30,
    p_change_type: params.change_type || null,
    p_field: params.field || null,
    p_limit: params.limit || 100,
  });
}

export function triggerCatalogSync(): Promise<{ ok: boolean; message?: string }> {
  // sync-catalog is cron-style (no CORS, x-cron-secret auth) — go through the
  // Station proxy, which authenticates staff and calls it server-side.
  return callFn("bar-admin-sync-catalog", {});
}

export async function getDeletedItemsReview(): Promise<{
  audit_date?: string;
  items: DeletedItemRow[];
}> {
  // Query latest deactivation_blocked entry from catalog_audit_log
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const logRes = await fetch(
    `${SUPABASE_URL}/rest/v1/catalog_audit_log?action=eq.deactivation_blocked&order=created_at.desc&limit=1`,
    { headers }
  );
  const logRows = await logRes.json().catch(() => []);
  if (!logRows || logRows.length === 0) return { items: [] };

  const audit = logRows[0];
  const squareIds: string[] = audit.square_item_ids || [];
  const itemNames: string[] = audit.item_names || [];
  const auditDate = audit.created_at;

  if (squareIds.length === 0) return { audit_date: auditDate, items: [] };

  // Load keep decisions from DB
  const keepRes = await fetch(
    `${SUPABASE_URL}/rest/v1/catalog_audit_log?action=eq.keep&source=eq.deleted-items-review&select=square_item_ids&order=created_at.desc&limit=50`,
    { headers }
  );
  const keepRows = await keepRes.json().catch(() => []);
  const reviewedSet = new Set<string>();
  for (const row of keepRows || []) {
    for (const id of row.square_item_ids || []) reviewedSet.add(id);
  }

  // Cross-reference menu_items
  const menuItemMap = new Map<string, any>();
  for (let i = 0; i < squareIds.length; i += 50) {
    const batch = squareIds.slice(i, i + 50);
    const encoded = encodeURIComponent(quoteFilterList(batch));
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/menu_items?select=id,name,is_active,last_synced_at,square_bottle_item_id,square_pour_item_id&or=(square_bottle_item_id.in.${encoded},square_pour_item_id.in.${encoded})`,
      { headers }
    );
    const rows = await res.json().catch(() => []);
    for (const row of rows || []) {
      if (row.square_bottle_item_id && batch.includes(row.square_bottle_item_id)) {
        menuItemMap.set(row.square_bottle_item_id, row);
      }
      if (row.square_pour_item_id && batch.includes(row.square_pour_item_id)) {
        if (!menuItemMap.has(row.square_pour_item_id)) {
          menuItemMap.set(row.square_pour_item_id, row);
        }
      }
    }
  }

  const items: DeletedItemRow[] = squareIds.map((sqId, idx) => {
    const match = menuItemMap.get(sqId);
    let refType: "bottle" | "pour" | "unknown" = "unknown";
    if (match) {
      if (match.square_bottle_item_id === sqId) refType = "bottle";
      else if (match.square_pour_item_id === sqId) refType = "pour";
    }
    return {
      id: match?.id,
      name: match?.name || itemNames[idx] || `Item ${sqId.slice(0, 8)}…`,
      square_id: sqId,
      ref_type: refType,
      is_active: match ? match.is_active : false,
      last_synced_at: match?.last_synced_at || null,
      audit_date: auditDate,
      is_reviewed: reviewedSet.has(sqId),
    };
  });

  return { audit_date: auditDate, items };
}

export async function resolveDeletedItems(
  items: DeletedItemRow[],
  action: "deactivate" | "keep"
): Promise<{ ok: boolean; count: number }> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const squareIds = items.map((i) => i.square_id);
  const dbIds = items.map((i) => i.id).filter(Boolean) as string[];

  if (action === "deactivate" && dbIds.length > 0) {
    const encoded = encodeURIComponent(quoteFilterList(dbIds));
    await fetch(`${SUPABASE_URL}/rest/v1/menu_items?id=in.${encoded}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify({ is_active: false }),
    });
  }

  // Record audit log
  await fetch(`${SUPABASE_URL}/rest/v1/catalog_audit_log`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      action: action === "deactivate" ? "deactivate_confirmed" : "keep",
      source: "deleted-items-review",
      square_item_ids: squareIds,
      item_names: items.map((i) => i.name),
      count: squareIds.length,
    }),
  });

  return { ok: true, count: squareIds.length };
}

// ── S3 Giving & Charity Ledger APIs ──

export interface CharitySummaryStats {
  this_quarter_cents?: number;
  this_year_cents?: number;
  all_time_cents?: number;
  all_time_tickets?: number;
}

export interface CharityItem {
  id: string;
  name: string;
  category_slug?: string | null;
  status?: string | null;
  charity_donation_cents?: number | null;
  charity_description?: string | null;
  item_type?: string | null;
  is_active?: boolean;
}

export interface CharityEventFund {
  id?: string;
  title: string;
  quarter?: number;
  year?: number;
  total_headcount?: number;
  total_raised_cents?: number;
}

export async function getCharitySummary(): Promise<CharitySummaryStats> {
  const rows = await callRpc<CharitySummaryStats[]>("get_charity_summary", {});
  return rows?.[0] || {};
}

export async function getCharityFunds(): Promise<CharityEventFund[]> {
  const rows = await callRpc<CharityEventFund[]>("get_charity_funds", {});
  return rows || [];
}

export async function listCharityItems(): Promise<CharityItem[]> {
  // Charity columns are staff-only (revoked from anon in Library's security
  // sweeps) — read via the Station service-role function instead of PostgREST.
  const res = await callFn<{ items: CharityItem[] }>("bar-admin-list-charity-items", {});
  return res.items || [];
}

export async function searchCatalogProducts(query: string): Promise<CharityItem[]> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/menu_items?name=ilike.*${encodeURIComponent(
      query
    )}*&is_active=eq.true&select=id,name,category_slug,item_type,status&order=name.asc&limit=15`,
    { headers }
  );
  if (!res.ok) return [];
  return res.json();
}

export async function upsertCharityItem(payload: {
  id: string;
  is_charity_item: boolean;
  charity_donation_cents?: number;
  charity_description?: string | null;
}): Promise<{ ok: boolean }> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
    Prefer: "return=minimal",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/menu_items?id=eq.${payload.id}`, {
    method: "PATCH",
    headers,
    body: JSON.stringify({
      is_charity_item: payload.is_charity_item,
      charity_donation_cents: payload.charity_donation_cents ?? 0,
      charity_description: payload.charity_description ?? null,
    }),
  });
  if (!res.ok) throw new Error("Failed to update charity item");
  return { ok: true };
}

// ── Cocktail Mapping APIs ──────────────────────────────────────────────────

export interface CocktailCoverageResponse {
  unmapped_drinks: number;
  unmapped_spirit_ingredients: number;
  recent_gaps: Array<{ created_at: string; reason?: string; metadata?: any }>;
}

export interface DrinkMappingItem {
  drink_id: string;
  drink_name: string;
  square_item_id: string | null;
  square_variation_ids?: string[];
}

export interface IngredientMappingItem {
  ingredient_id: string;
  ingredient_name: string;
  menu_item_id: string | null;
}

export interface SquareSearchItem {
  square_item_id: string;
  name: string;
  variation_ids: string[];
}

export interface PourMenuItem {
  id: string;
  name: string;
  status: string;
  pours_remaining: number | null;
}

export function getCocktailMappingCoverage(): Promise<CocktailCoverageResponse> {
  return callFn<CocktailCoverageResponse>("cocktail-map-admin", { action: "coverage" });
}

export function listDrinksForMapping(): Promise<{ drinks: DrinkMappingItem[] }> {
  return callFn<{ drinks: DrinkMappingItem[] }>("cocktail-map-admin", { action: "list_drinks" });
}

export function listSpiritIngredientsForMapping(): Promise<{ ingredients: IngredientMappingItem[] }> {
  return callFn<{ ingredients: IngredientMappingItem[] }>("cocktail-map-admin", { action: "list_spirit_ingredients" });
}

export function listPourItemsForMapping(): Promise<{ pours: PourMenuItem[] }> {
  return callFn<{ pours: PourMenuItem[] }>("cocktail-map-admin", { action: "list_pour_items" });
}

export function searchSquareItemsForMapping(): Promise<{ items: SquareSearchItem[] }> {
  return callFn<{ items: SquareSearchItem[] }>("cocktail-map-admin", { action: "search_square_items" });
}

export function upsertCocktailMap(payload: {
  drink_id: string;
  drink_name?: string;
  square_item_id: string;
  notes?: string;
}): Promise<{ map: any; variation_count: number }> {
  return callFn("cocktail-map-admin", { action: "upsert_cocktail_map", ...payload });
}

export function upsertIngredientMap(payload: {
  ingredient_id: string;
  ingredient_name?: string;
  menu_item_id: string;
  notes?: string;
}): Promise<{ map: any }> {
  return callFn("cocktail-map-admin", { action: "upsert_ingredient_map", ...payload });
}

export function deactivateCocktailMap(drink_id: string): Promise<{ ok: boolean }> {
  return callFn("cocktail-map-admin", { action: "deactivate_cocktail_map", drink_id });
}

export function deactivateIngredientMap(ingredient_id: string): Promise<{ ok: boolean }> {
  return callFn("cocktail-map-admin", { action: "deactivate_ingredient_map", ingredient_id });
}

// ── Events Manager APIs ───────────────────────────────────────────────────

// Every column except id/name is nullable in public.event_templates
// (checked against information_schema 2026-09-12). applyTemplate guards on that.
export interface EventTemplate {
  id: string;
  name: string;
  description_template: string | null;
  event_type: string | null;
  location: string | null;
  default_capacity: number | null;
  default_price_cents: number | null;
  default_c33_price_cents: number | null;
  default_rsvp_enabled: boolean | null;
  default_tags: string[] | null;
  title_pattern: string | null;
  metadata: any;
}

export async function listEventTemplates(): Promise<EventTemplate[]> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const url = `${SUPABASE_URL}/rest/v1/event_templates?select=*&order=name.asc`;
  const res = await fetch(url, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new ApiError(err.message || "Failed to load event templates", res.status);
  }
  return res.json();
}

export interface CalendarEvent {
  id?: string;
  title: string;
  event_type: string;
  status: "published" | "pending" | "draft" | "past" | "canceled";
  series_id?: string | null;
  start_at: string;
  end_at?: string | null;
  location?: string | null;
  location_address?: string | null;
  capacity?: number | null;
  rsvp_type: "rsvp" | "signal" | "donation_signal" | "ticket" | "donation" | "none";
  price_cents?: number;
  c33_price_cents?: number | null;
  contains_alcohol: boolean;
  age_requirement?: boolean | null;
  description?: string | null;
  slug?: string | null;
  published_at?: string | null;
  created_by?: string | null;
  image_url?: string | null;
  square_checkout_url?: string | null;
    square_catalog_object_id?: string | null;
  tags?: string[] | null;
  public_at?: string | null;
  attendee_count?: number;
  // Offline totals (staff-only "Save Totals" in the editor) — real column names,
  // the port previously guessed *_donations_cents / unnamed_attendee_count (L66).
  offline_cash_cents?: number | null;
  offline_card_cents?: number | null;
  manual_attendee_count?: number | null;
  doors_at?: string | null;
  rsvp_enabled?: boolean;
  ticket_required?: boolean;
  donation_enabled?: boolean;
  planning_signal_enabled?: boolean;
  distillery_ids?: string[];
  partners?: Array<{ partner_id: string; role: string }>;
  c33_note?: string | null;
  c33_note_enabled?: boolean;
  /**
   * Free-form jsonb. Known keys: `address` (venue address), `c33_member_note`
   * (false = block disabled, string = custom text, null/absent = default text),
   * and `gbp_post_id` / `fb_post_id` / `ig_media_id` written by `publish-event`.
   * Always merge on save — `submit-form` replaces the whole column.
   */
  metadata?: Record<string, any> | null;
}

export interface EventAttendee {
  id: string;
  event_id: string;
  customer_id?: string | null;
  customer_name: string;
  customer_email?: string | null;
  customer_phone?: string | null;
  quantity: number;
  ticket_tier: string;
  payment_status: string;
  checked_in_at?: string | null;
  notes?: string | null;
  created_at: string;
}

export async function listAdminEvents(statusFilter: string = "all"): Promise<CalendarEvent[]> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  let url = `${SUPABASE_URL}/rest/v1/calendar_events?select=*&order=start_at.desc&limit=100`;
  if (statusFilter !== "all") {
    url += `&status=eq.${statusFilter}`;
  }
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error(`Failed to load events: HTTP ${res.status}`);
  return res.json();
}

export async function saveAdminEvent(eventData: Partial<CalendarEvent>): Promise<CalendarEvent> {
  // Routes through staff-gated submit-form edge function to write with service-role permissions
  const payload = {
    ...eventData,
    updated_at: new Date().toISOString(),
  };
  const res = await callFn<{ ok: boolean; data?: CalendarEvent[] }>("submit-form", {
    action: "save-event",
    ...payload,
  });
  if (!res.ok) throw new Error("Save event failed");
  const saved = (Array.isArray(res.data) ? res.data[0] : res.data) || payload;
  return saved as CalendarEvent;
}

export async function deleteAdminEvent(eventId: string): Promise<void> {
  const res = await callFn<{ ok: boolean }>("submit-form", {
    action: "save-event",
    id: eventId,
    status: "canceled",
  });
  if (!res.ok) throw new Error("Cancel event failed");
}

export async function listEventAttendees(eventId: string): Promise<EventAttendee[]> {
  try {
    const res = await callFn<{ registrations: any[] }>("purchase-event-ticket", {
      action: "admin-list-attendees",
      event_id: eventId,
    });
    return (res.registrations || []).map((r: any) => ({
      id: r.id,
      event_id: r.event_id,
      customer_id: r.customer_id,
      customer_name:
        r.customer?.display_name ||
        `${r.customer?.first_name || ""} ${r.customer?.last_name || ""}`.trim() ||
        "Guest",
      customer_email: r.customer?.email || null,
      customer_phone: r.customer?.phone || null,
      quantity: r.quantity || 1,
      ticket_tier: r.ticket_type || "general",
      payment_status: r.status || "confirmed",
      checked_in_at: r.checked_in_at || null,
      notes: r.notes || null,
      created_at: r.registered_at || r.created_at || new Date().toISOString(),
    }));
  } catch (err) {
    console.error("listEventAttendees error:", err);
    throw err;
  }
}

export interface CustomerSearchResult {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  has_card: boolean;
  is_c33: boolean;
}

export async function searchCustomersForEvent(query: string): Promise<CustomerSearchResult[]> {
  const res = await callFn<{ results: CustomerSearchResult[] }>("purchase-event-ticket", {
    action: "admin-search-customers",
    query,
  });
  return res.results || [];
}

export async function addEventAttendee(payload: {
  event_id: string;
  customer_id: string;
  quantity: number;
  ticket_tier: string;
  price_override_cents?: number;
  notes?: string | null;
}): Promise<any> {
  return callFn("purchase-event-ticket", {
    action: "admin-register",
    event_id: payload.event_id,
    customer_id: payload.customer_id,
    ticket_type: payload.ticket_tier,
    quantity: payload.quantity,
    price_override_cents: payload.price_override_cents,
    notes: payload.notes,
  });
}

// ── Reviews Moderation APIs ────────────────────────────────────────────────

export interface DistilleryReview {
  id: string;
  rating: number;
  review_text: string | null;
  visit_date: string | null;
  highlights: string[] | null;
  would_recommend: boolean | null;
  helpful_count: number;
  status: "published" | "flagged" | "hidden";
  distillery_response: string | null;
  created_at: string;
  customer_id: string | null;
  customers?: { display_name: string | null };
  distillery_id: string | null;
  distilleries?: { name: string | null; slug: string | null };
}

export async function listAdminReviews(statusFilter: string = "all"): Promise<DistilleryReview[]> {
  const res = await callFn<{ ok: boolean; reviews: DistilleryReview[] }>("distillery-reviews", {
    action: "list_reviews",
    status: statusFilter,
  });
  return res.reviews || [];
}

export function moderateAdminReview(reviewId: string, status: "published" | "flagged" | "hidden"): Promise<any> {
  return callFn("distillery-reviews", { action: "moderate", review_id: reviewId, status });
}

export function respondToAdminReview(reviewId: string, responseText: string): Promise<any> {
  return callFn("distillery-reviews", { action: "respond", review_id: reviewId, response_text: responseText });
}

/** AI draft for a Google review (google_reviews.id). Library edge function;
 * staff-gated (owner/manager/order_manager) and saves the draft server-side. */
export function generateGoogleReviewDraft(reviewId: string): Promise<{ draft?: string }> {
  return callFn<{ draft?: string }>("generate-review-reply", { action: "generate", review_id: reviewId });
}

// ── Google Business Profile reviews (ported from the Library's /reviews page) ──
// The edge functions (sync-reviews, generate-review-reply, reply-review) live in
// the Library repo and require owner/manager/order_manager in customers.staff_roles.

export interface GoogleReview {
  id: string;
  reviewer_name: string | null;
  reviewer_photo_url: string | null;
  star_rating: number | null;
  comment: string | null;
  review_created_at: string | null;
  reply_text: string | null;
  reply_updated_at: string | null;
  ai_draft_text: string | null;
  sentiment: "positive" | "neutral" | "negative" | null;
}

export interface GoogleReviewStats {
  total?: number;
  unreplied?: number;
  avg_rating?: number | string | null;
  avg_response_minutes?: number | string | null;
}

// Read through the manager-gated edge function, never google_reviews' public
// SELECT policy (L51) — a Library lockdown would silently empty the tab.
export async function listGoogleReviews(): Promise<GoogleReview[]> {
  const res = await callFn<{ reviews?: GoogleReview[] }>("sync-reviews", { action: "list" });
  return res.reviews ?? [];
}

export function getGoogleReviewStats(): Promise<GoogleReviewStats> {
  return callRpc<GoogleReviewStats>("get_review_stats", {});
}

export function syncGoogleReviews(): Promise<{ synced?: number; new_reviews?: number }> {
  return callFn("sync-reviews", { action: "sync" }, { timeoutMs: 60_000 });
}

export function sendGoogleReviewReply(reviewId: string, replyText: string): Promise<unknown> {
  return callFn("reply-review", { action: "send-reply", review_id: reviewId, reply_text: replyText });
}

// ── Pick List APIs ─────────────────────────────────────────────────────────

export interface PickListBottle {
  id: string;
  name: string;
  item_type: string;
  category_slug: string | null;
  subcategory: string | null;
  shelf_location: string[] | null;
  inventory_count: number | null;
  reorder_point: number | null;
  status: string | null;
  bottle_price_cents?: number | null;
  pour_price_cents?: number | null;
  sku?: string | null;
  upc?: string | null;
  distillery?: string | null;
}

export async function getInventoryForPickList(): Promise<PickListBottle[]> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  return readAllRows<PickListBottle>(async (from, to) => {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/menu_items?is_active=eq.true&select=id,name,item_type,category_slug,subcategory,shelf_location,inventory_count,reorder_point,status,bottle_price_cents,pour_price_cents,sku,upc,distillery&order=name.asc,id.asc&offset=${from}&limit=${to - from + 1}`,
      { headers }
    );
    if (!res.ok) throw new Error("Failed to load pick list inventory");
    return { data: await res.json(), error: null };
  });
}

// ── Inventory Intelligence & Cycle Counts ──────────────────────────────────

export interface InventoryKPIs {
  total_items?: number;
  total_value_cents?: number;
  fast_velocity_count?: number;
  moderate_velocity_count?: number;
  slow_velocity_count?: number;
  dead_stock_count?: number;
  avg_days_on_hand?: number;
}

export interface InventoryUsageItem {
  menu_item_id: string;
  name: string;
  item_type: string;
  category: string | null;
  shelf_code: string | null;
  current_inventory: number | null;
  cost_cents: number | null;
  units_30d: number | null;
  velocity: "fast" | "moderate" | "slow" | "dead";
  days_on_hand: number | null;
}

export interface InventoryCountHistoryRow {
  id: string;
  started_at: string;
  completed_at: string | null;
  counted_by_name: string | null;
  count_type: string;
  status: "in_progress" | "completed" | "cancelled";
  items_counted: number;
  total_variance_units: number;
  total_variance_cost_cents: number;
  notes: string | null;
}

export interface CountItemRow {
  menu_item_id: string;
  name: string;
  item_type: string;
  category: string | null;
  system_count: number | null;
  cost_cents: number | null;
  already_counted: boolean;
  count_full: number;
  count_partial_pct: number;
  sku?: string | null;
  upc?: string | null;
}

export interface CountVarianceRow {
  menu_item_id: string;
  name: string;
  item_type: string;
  effective_count: number;
  system_count: number | null;
  variance: number;
  variance_cost_cents: number | null;
  count_partial_pct: number;
}

export async function getInventoryKPIs(): Promise<InventoryKPIs> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/get_inventory_kpis`, {
    method: "POST",
    headers,
    body: JSON.stringify({}),
  });
  if (!res.ok) throw new Error("Failed to load inventory KPIs");
  return res.json();
}

export async function getInventoryUsage(params: {
  item_type?: string | null;
  velocity?: string | null;
  search?: string | null;
  sort?: string;
  limit?: number;
}): Promise<InventoryUsageItem[]> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/get_inventory_usage`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      p_item_type: params.item_type || null,
      p_velocity: params.velocity || null,
      p_search: params.search || null,
      p_sort: params.sort || "units_30d",
      p_limit: params.limit || 100,
    }),
  });
  if (!res.ok) throw new Error("Failed to load inventory usage");
  return res.json();
}

/**
 * Every usage row (costs + velocity). The RPC has no offset, but PostgREST
 * applies ?offset/limit/order to its output, and max_rows caps one call at
 * 1000 whatever p_limit says. menu_item_id is unique, so pages are stable.
 */
export async function getAllInventoryUsage(): Promise<InventoryUsageItem[]> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  return readAllRows<InventoryUsageItem>(async (from, to) => {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/rpc/get_inventory_usage?order=menu_item_id.asc&offset=${from}&limit=${to - from + 1}`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ p_item_type: null, p_velocity: null, p_search: null, p_sort: "units_30d", p_limit: 100000 }),
      }
    );
    if (!res.ok) throw new Error("Failed to load inventory usage");
    return { data: await res.json(), error: null };
  });
}

export async function getInventoryCountHistory(limit = 25): Promise<InventoryCountHistoryRow[]> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/get_inventory_count_history`, {
    method: "POST",
    headers,
    body: JSON.stringify({ p_limit: limit }),
  });
  if (!res.ok) throw new Error("Failed to load count history");
  return res.json();
}

export async function startInventoryCount(countType = "full", notes?: string | null): Promise<string> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/start_inventory_count`, {
    method: "POST",
    headers,
    body: JSON.stringify({ p_count_type: countType, p_notes: notes || null }),
  });
  if (!res.ok) throw new Error("Failed to start inventory count session");
  return res.json();
}

export async function getCountItems(sessionId: string, search?: string | null, itemType?: string | null): Promise<CountItemRow[]> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/get_count_items`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      p_session_id: sessionId,
      p_search: search || null,
      p_item_type: itemType || null,
    }),
  });
  if (!res.ok) throw new Error("Failed to load count items");
  return res.json();
}

export async function recordItemCount(payload: {
  session_id: string;
  menu_item_id: string;
  count_full: number;
  count_partial_pct?: number;
}): Promise<void> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/record_item_count`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      p_session_id: payload.session_id,
      p_menu_item_id: payload.menu_item_id,
      p_count_full: payload.count_full,
      p_count_partial_pct: payload.count_partial_pct || 0,
    }),
  });
  if (!res.ok) throw new Error("Failed to record count");
}

export async function completeInventoryCount(sessionId: string): Promise<{ items_counted: number; total_variance_units: number; total_variance_cost_cents: number }> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/complete_inventory_count`, {
    method: "POST",
    headers,
    body: JSON.stringify({ p_session_id: sessionId }),
  });
  if (!res.ok) throw new Error("Failed to complete count");
  return res.json();
}

export async function getCountVariance(sessionId: string): Promise<CountVarianceRow[]> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/get_count_variance`, {
    method: "POST",
    headers,
    body: JSON.stringify({ p_session_id: sessionId }),
  });
  if (!res.ok) throw new Error("Failed to load count variance");
  return res.json();
}

// ── Notification & Email Templates ─────────────────────────────────────────

export interface EmailTemplate {
  id: string;
  name: string;
  slug: string;
  template_key: string;
  subject: string;
  text_body: string | null;
  html_body: string;
  category: "campaign" | "event" | "lifecycle" | "membership" | "system" | string;
  is_active: boolean;
  updated_at: string;
  metadata?: Record<string, any> | null;
}

export async function listEmailTemplates(): Promise<EmailTemplate[]> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/email_templates?select=*&order=category.asc,name.asc`, {
    headers,
  });
  if (!res.ok) throw new Error("Failed to load email templates");
  return res.json();
}

export async function saveEmailTemplate(template: Partial<EmailTemplate>): Promise<EmailTemplate> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
    Prefer: "return=representation",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const isEdit = !!template.id;
  const url = isEdit
    ? `${SUPABASE_URL}/rest/v1/email_templates?id=eq.${template.id}`
    : `${SUPABASE_URL}/rest/v1/email_templates`;

  const payload = {
    ...template,
    updated_at: new Date().toISOString(),
  };

  const res = await fetch(url, {
    method: isEdit ? "PATCH" : "POST",
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("Failed to save template");
  const data = await res.json();
  return Array.isArray(data) ? data[0] : data;
}

export async function deleteEmailTemplate(templateId: string): Promise<void> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/email_templates?id=eq.${templateId}`, {
    method: "DELETE",
    headers,
  });
  if (!res.ok) throw new Error("Failed to delete template");
}

// ── Partners & Community ───────────────────────────────────────────────────

export interface Partner {
  id: string;
  name: string;
  slug: string;
  partner_type: "organization" | "charity" | "brand" | "media" | "individual" | "distributor" | string;
  description: string | null;
  website_url: string | null;
  logo_url: string | null;
  facebook_handle: string | null;
  facebook_page_id: string | null;
  instagram_handle: string | null;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

export async function listPartners(): Promise<Partner[]> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/partners?select=*&order=name.asc`, { headers });
  if (!res.ok) throw new Error("Failed to load partners");
  return res.json();
}

export async function savePartner(partner: Partial<Partner>): Promise<Partner> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
    Prefer: "return=representation",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const isEdit = !!partner.id;
  const url = isEdit
    ? `${SUPABASE_URL}/rest/v1/partners?id=eq.${partner.id}`
    : `${SUPABASE_URL}/rest/v1/partners`;

  const payload = {
    ...partner,
    updated_at: new Date().toISOString(),
  };

  const res = await fetch(url, {
    method: isEdit ? "PATCH" : "POST",
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("Failed to save partner");
  const data = await res.json();
  return Array.isArray(data) ? data[0] : data;
}

export async function deletePartner(partnerId: string): Promise<void> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/partners?id=eq.${partnerId}`, {
    method: "DELETE",
    headers,
  });
  if (!res.ok) throw new Error("Failed to delete partner");
}

// ── Articles & Dispatches ──────────────────────────────────────────────────

export interface Article {
  id: string;
  title: string;
  slug: string;
  article_type: "education" | "journal" | string;
  status: "published" | "draft" | string;
  excerpt: string | null;
  content_markdown: string | null;
  cover_image_url: string | null;
  reading_time_minutes: number | null;
  tags: string[] | null;
  published_at: string | null;
  created_at?: string;
  updated_at?: string;
}

export async function listArticles(): Promise<Article[]> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/articles?select=*&order=created_at.desc`, { headers });
  if (!res.ok) throw new Error("Failed to load articles");
  return res.json();
}

export async function saveArticle(article: Partial<Article>): Promise<Article> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
    Prefer: "return=representation",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const isEdit = !!article.id;
  const url = isEdit
    ? `${SUPABASE_URL}/rest/v1/articles?id=eq.${article.id}`
    : `${SUPABASE_URL}/rest/v1/articles`;

  const payload = {
    ...article,
    updated_at: new Date().toISOString(),
  };

  const res = await fetch(url, {
    method: isEdit ? "PATCH" : "POST",
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("Failed to save article");
  const data = await res.json();
  return Array.isArray(data) ? data[0] : data;
}

export async function deleteArticle(articleId: string): Promise<void> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/articles?id=eq.${articleId}`, {
    method: "DELETE",
    headers,
  });
  if (!res.ok) throw new Error("Failed to delete article");
}

/**
 * "Dispatches" originally read a nonexistent `dispatches` table (L51). The
 * real artifact is Library's `dispatch_runs` — the article-discovery pipeline
 * run log (service_role-only RLS). Approved shape (REPAIR_PLAN D2): read-only
 * run history via the Station service-role function; there is no per-article
 * review workflow here.
 */
export interface DispatchRun {
  id: string;
  started_at: string;
  completed_at: string | null;
  status: string;
  distilleries_queried: number | null;
  articles_found: number | null;
  articles_verified: number | null;
  articles_approved: number | null;
  articles_rejected: number | null;
  created_at: string;
}

export async function listDispatchRuns(): Promise<DispatchRun[]> {
  const res = await callFn<{ runs: DispatchRun[] }>("bar-admin-list-dispatch-runs", {});
  return res.runs || [];
}

// ── Bottle Photos Moderation ───────────────────────────────────────────────

export interface BottlePhoto {
  id: string;
  bottle_id: string;
  bottle_name?: string;
  distillery_name?: string;
  photo_url: string;
  caption: string | null;
  submitted_by_name?: string | null;
  submitted_by_email?: string | null;
  status: "pending" | "approved" | "rejected" | "featured" | string;
  is_featured?: boolean;
  created_at: string;
}

/**
 * Member bottle photos live in Library's `bottle_photo_submissions` (the
 * `bottle_photos` table this section originally used never existed — L51).
 * Anon RLS exposes approved rows only; pending moderation goes through
 * Library's staff functions bottle-photo-list-pending / -approve / -reject
 * (owner/manager role required). Storage public URL base matches the
 * `bottle-photos` bucket those functions use.
 */
const BOTTLE_PHOTO_URL_BASE = () => `${SUPABASE_URL}/storage/v1/object/public/bottle-photos/`;

export async function listBottlePhotos(statusFilter = "pending"): Promise<BottlePhoto[]> {
  if (statusFilter === "pending" || statusFilter === "all") {
    const res = await callFn<{ submissions: any[] }>("bottle-photo-list-pending", null, { method: "GET" });
    const pending = (res.submissions || []).map((s: any) => ({
      id: s.id,
      bottle_id: s.menu_item_id,
      bottle_name: s.bottle?.name || "Unknown Bottle",
      distillery_name: s.bottle?.distillery || null,
      photo_url: s.public_url,
      caption: s.caption ?? null,
      submitted_by_name: s.submitter?.first_name || null,
      status: "pending",
      created_at: s.submitted_at,
    }));
    if (statusFilter === "pending") return pending;
    return [...pending, ...(await listApprovedBottlePhotos())];
  }
  if (statusFilter === "approved") return listApprovedBottlePhotos();
  // Rejected rows are not visible to anon RLS and have no staff list endpoint.
  return [];
}

async function listApprovedBottlePhotos(): Promise<BottlePhoto[]> {
  const headers: Record<string, string> = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) headers["x-customer-token"] = token;

  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/bottle_photo_submissions?status=eq.approved&select=id,menu_item_id,caption,storage_path,submitted_at,menu_items(name,distillery)&order=submitted_at.desc&limit=200`,
    { headers }
  );
  if (!res.ok) throw new Error("Failed to load bottle photos");
  const data = await res.json();
  return (data || []).map((p: any) => ({
    id: p.id,
    bottle_id: p.menu_item_id,
    bottle_name: p.menu_items?.name || "Unknown Bottle",
    distillery_name: p.menu_items?.distillery || null,
    photo_url: BOTTLE_PHOTO_URL_BASE() + p.storage_path,
    caption: p.caption ?? null,
    status: "approved",
    created_at: p.submitted_at,
  }));
}

export async function moderateBottlePhoto(
  photoId: string,
  status: "approved" | "rejected" | "featured",
  setAsPrimary = false,
  bottleId?: string,
  photoUrl?: string
): Promise<void> {
  if (status === "rejected") {
    await callFn("bottle-photo-reject", { submission_id: photoId, reason: "Rejected by Station staff" });
    return;
  }
  // "featured" has no server-side equivalent — treat as approve. The approve
  // function promotes the photo to menu_items.community_image_url itself when
  // the bottle has no manual image.
  await callFn("bottle-photo-approve", { submission_id: photoId });

  if (setAsPrimary && bottleId && photoUrl) {
    // Explicit staff override: set the manual image (wins over community).
    const headers: Record<string, string> = {
      apikey: SUPABASE_PUBLISHABLE_KEY,
      "Content-Type": "application/json",
    };
    const token = getStoredToken();
    if (token) headers["x-customer-token"] = token;
    const res = await fetch(`${SUPABASE_URL}/rest/v1/menu_items?id=eq.${bottleId}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify({ manual_image_url: photoUrl }),
    });
    if (!res.ok) throw new Error("Photo approved, but setting it as the primary image failed");
  }
}

export async function deleteBottlePhoto(photoId: string): Promise<void> {
  // No delete endpoint exists; rejection is the supported removal path and
  // records a reason for the submitter.
  await callFn("bottle-photo-reject", { submission_id: photoId, reason: "Removed by Station staff" });
}

// ── Whiskey Intelligence & Allocations ────────────────────────────────────

export interface RepInteraction {
  id: string;
  rep_name: string;
  distributor: string;
  contact_type: "in_person" | "email" | "phone" | "text" | string;
  notes: string | null;
  interaction_date: string;
  follow_up_date: string | null;
  follow_up_needed: boolean;
  created_at: string;
}

export interface AllocationRequest {
  id: string;
  distributor: string;
  bottle_name: string;
  cases_requested: number;
  cases_granted: number | null;
  status: "pending" | "granted" | "denied" | "partial" | "ghosted" | string;
  requested_at: string;
  notes: string | null;
}

export async function listRepInteractions(): Promise<RepInteraction[]> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/rep_interactions?select=*&order=interaction_date.desc`, {
    headers,
  });
  if (!res.ok) throw new Error("Failed to load rep interactions");
  return res.json();
}

export async function saveRepInteraction(entry: Partial<RepInteraction>): Promise<RepInteraction> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
    Prefer: "return=representation",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const isEdit = !!entry.id;
  const url = isEdit
    ? `${SUPABASE_URL}/rest/v1/rep_interactions?id=eq.${entry.id}`
    : `${SUPABASE_URL}/rest/v1/rep_interactions`;

  const res = await fetch(url, {
    method: isEdit ? "PATCH" : "POST",
    headers,
    body: JSON.stringify(entry),
  });
  if (!res.ok) throw new Error("Failed to save interaction");
  const data = await res.json();
  return Array.isArray(data) ? data[0] : data;
}

export async function listAllocationRequests(): Promise<AllocationRequest[]> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/allocation_requests?select=*&order=requested_at.desc`, {
    headers,
  });
  if (!res.ok) throw new Error("Failed to load allocations");
  return res.json();
}

export async function saveAllocationRequest(entry: Partial<AllocationRequest>): Promise<AllocationRequest> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
    Prefer: "return=representation",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const isEdit = !!entry.id;
  const url = isEdit
    ? `${SUPABASE_URL}/rest/v1/allocation_requests?id=eq.${entry.id}`
    : `${SUPABASE_URL}/rest/v1/allocation_requests`;

  const res = await fetch(url, {
    method: isEdit ? "PATCH" : "POST",
    headers,
    body: JSON.stringify(entry),
  });
  if (!res.ok) throw new Error("Failed to save allocation");
  const data = await res.json();
  return Array.isArray(data) ? data[0] : data;
}

// ── Trails & Tasting Passports ─────────────────────────────────────────────

export interface Trail {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  region: "kc_local" | "missouri" | "regional" | "national" | "international" | string | null;
  duration: string | null;
  difficulty: "easy" | "moderate" | "road_trip" | string | null;
  status: "draft" | "published" | string;
  c33_exclusive: boolean;
  stops_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface TrailStop {
  id: string;
  trail_id: string;
  distillery_id: string | null;
  distillery_name?: string;
  stop_order: number;
  notes: string | null;
}

/**
 * Trails live in Library's `curated_trails` / `curated_trail_stops` tables
 * (the `trails`/`trail_stops` names this section originally used never
 * existed — L51). Access model:
 *   - READS via bar-admin-list-trails (service role) — the public RLS is
 *     published-only, so anon PostgREST cannot see draft trails or any stops
 *     of a draft, which would break the create → add stops → publish flow.
 *   - WRITES via Library's `curated-trails` function (staff-token auth) —
 *     never write Library-owned public.* tables from Station.
 * The UI's `duration` field maps to the real `estimated_duration` column.
 */

export async function listTrails(): Promise<Trail[]> {
  const res = await callFn<{ trails: any[] }>("bar-admin-list-trails", { action: "list_trails" });
  return (res.trails || []).map((t: any) => ({
    ...t,
    duration: t.estimated_duration ?? null,
    stops_count: t.curated_trail_stops?.[0]?.count || 0,
  }));
}

export async function saveTrail(trail: Partial<Trail>): Promise<Trail> {
  const isEdit = !!trail.id;
  const fields: Record<string, unknown> = {
    name: trail.name,
    description: trail.description ?? null,
    region: trail.region ?? null,
    estimated_duration: trail.duration ?? null,
    difficulty: trail.difficulty ?? null,
    c33_exclusive: trail.c33_exclusive ?? false,
  };
  if (isEdit && trail.status) fields.status = trail.status;
  if (isEdit && trail.slug) fields.slug = trail.slug;

  const res = await callFn<{ ok: boolean; trail: any }>(
    "curated-trails",
    isEdit ? { action: "update", trail_id: trail.id, ...fields } : { action: "create", ...fields }
  );
  const t = res.trail;
  return { ...t, duration: t?.estimated_duration ?? null };
}

export async function deleteTrail(trailId: string): Promise<void> {
  await callFn("curated-trails", { action: "delete", trail_id: trailId });
}

export async function listTrailStops(trailId: string): Promise<TrailStop[]> {
  const res = await callFn<{ stops: any[] }>("bar-admin-list-trails", {
    action: "list_stops",
    trail_id: trailId,
  });
  return (res.stops || []).map((s: any) => ({
    ...s,
    distillery_name: s.distilleries?.name || "Unknown Distillery",
  }));
}

/**
 * Replace ALL stops of a trail (Library's `set_stops` action deletes and
 * re-inserts). Callers must pass the complete desired list — there is no
 * per-stop save/delete, because a partial write would wipe the rest.
 */
export async function setTrailStops(
  trailId: string,
  stops: Array<Pick<TrailStop, "distillery_id" | "notes">>
): Promise<void> {
  await callFn("curated-trails", { action: "set_stops", trail_id: trailId, stops });
}

/** Best-effort distillery lookup by name for attaching trail stops. */
export async function findDistilleryByName(name: string): Promise<{ id: string; name: string } | null> {
  const headers: Record<string, string> = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) headers["x-customer-token"] = token;
  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/distilleries?name=ilike.*${encodeURIComponent(name.trim())}*&select=id,name&limit=1`,
    { headers }
  );
  if (!res.ok) return null;
  const rows = await res.json();
  return rows?.[0] ?? null;
}

// ── Suggested Edits ────────────────────────────────────────────────────────

export interface SuggestedEdit {
  id: string;
  item_id: string;
  item_name?: string;
  field_name: string;
  old_value: string | null;
  new_value: string;
  suggested_by_name?: string | null;
  submitter_trust?: "high" | "med" | "low" | "none" | string;
  status: "pending" | "approved" | "rejected" | string;
  source_url: string | null;
  created_at: string;
}

/** Bucket Library's numeric ai_trust_score into the UI's trust labels. */
function trustBucket(score: number | null | undefined): SuggestedEdit["submitter_trust"] {
  if (score == null) return "none";
  if (score >= 0.8) return "high";
  if (score >= 0.5) return "med";
  return "low";
}

/**
 * Suggested edits live in Library's polymorphic `suggested_edits` table
 * (entity_type/entity_id — there is no FK to menu_items, and anon can only
 * SELECT pending rows, with no write grants at all). Both listing and review
 * go through Library's `manage-suggested-edits` function, which authenticates
 * the staff token and applies approved values to the target entity itself.
 * NOTE: that function requires the main-app `owner`/`manager` role strings —
 * Station-only `bar_manager` accounts (without the alias role granted) get 403.
 */
export async function listSuggestedEdits(statusFilter = "pending"): Promise<SuggestedEdit[]> {
  const res = await callFn<{ edits: any[] }>("manage-suggested-edits", {
    action: "list",
    status_filter: statusFilter,
    per_page: 100,
  });
  // Staff responses arrive with entity_name/entity_slug already enriched.
  return (res.edits || []).map((e: any) => ({
    ...e,
    item_id: e.entity_id,
    item_name: e.entity_name || "Unknown Item",
    suggested_by_name: e.customers?.display_name || null,
    submitter_trust: trustBucket(e.ai_trust_score),
    source_url: Array.isArray(e.ai_sources) ? e.ai_sources[0]?.url || null : null,
  }));
}

export async function moderateSuggestedEdit(
  editId: string,
  status: "approved" | "rejected",
  _itemId?: string,
  _fieldName?: string,
  appliedValue?: string
): Promise<void> {
  // The `review` action applies the approved value to the target entity
  // server-side — no client-side menu_items patch needed (or wanted).
  await callFn("manage-suggested-edits", {
    action: "review",
    edit_id: editId,
    decision: status,
    ...(appliedValue !== undefined ? { applied_value: appliedValue } : {}),
  });
}

// ── Business Context & External Events ─────────────────────────────────────

export interface BusinessEvent {
  id: string;
  event_date: string;
  date?: string;
  event_name: string;
  event_type: "sports" | "concert" | "weather" | "holiday" | "convention" | "local" | "school" | string;
  event_subtype?: string | null;
  venue?: string | null;
  expected_impact?: "high_positive" | "moderate_positive" | "neutral" | "negative" | string;
  notes: string | null;
  created_at?: string;
}

export async function listBusinessEvents(): Promise<BusinessEvent[]> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/business_events?select=*&order=date.desc&limit=200`, {
    headers,
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    throw new Error(`Failed to load business events: ${errText || res.statusText}`);
  }
  const rawList: any[] = await res.json();
  return rawList.map((r) => ({
    id: r.id,
    event_date: r.date || r.event_date || "",
    date: r.date,
    event_name: r.event_name || "",
    event_type: r.event_type || "local",
    event_subtype: r.event_subtype,
    venue: r.venue,
    expected_impact: r.expected_impact || (r.event_type === "school" || r.event_type === "sports" || r.event_type === "concert" ? "moderate_positive" : "neutral"),
    notes: r.notes || "",
    created_at: r.created_at,
  }));
}

export async function saveBusinessEvent(entry: Partial<BusinessEvent>): Promise<BusinessEvent> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
    Prefer: "return=representation",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const payload: any = {
    date: entry.event_date || entry.date,
    event_name: entry.event_name,
    event_type: entry.event_type,
    notes: entry.notes || null,
  };
  if (entry.venue !== undefined) payload.venue = entry.venue;
  if (entry.event_subtype !== undefined) payload.event_subtype = entry.event_subtype;

  const isEdit = !!entry.id;
  const url = isEdit
    ? `${SUPABASE_URL}/rest/v1/business_events?id=eq.${entry.id}`
    : `${SUPABASE_URL}/rest/v1/business_events`;

  const res = await fetch(url, {
    method: isEdit ? "PATCH" : "POST",
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    throw new Error(`Failed to save business event: ${errText || res.statusText}`);
  }
  const rawData = await res.json();
  const r = Array.isArray(rawData) ? rawData[0] : rawData;
  return {
    id: r.id,
    event_date: r.date || r.event_date || "",
    date: r.date,
    event_name: r.event_name || "",
    event_type: r.event_type || "local",
    event_subtype: r.event_subtype,
    venue: r.venue,
    expected_impact: r.expected_impact || "neutral",
    notes: r.notes || "",
    created_at: r.created_at,
  };
}

export async function deleteBusinessEvent(id: string): Promise<void> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/business_events?id=eq.${id}`, {
    method: "DELETE",
    headers,
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    throw new Error(`Failed to delete business event: ${errText || res.statusText}`);
  }
}

// ── Knowledge Base & Staff SOPs ────────────────────────────────────────────

export interface KnowledgeArticle {
  id: string;
  title: string;
  slug: string;
  category: "bar_operations" | "opening_closing" | "guest_experience" | "inventory" | "emergency" | "memberships" | "general" | string;
  body: string;
  video_url: string | null;
  is_published: boolean;
  tags?: string[] | null;
  created_at?: string;
  updated_at?: string;
}

export interface AskSentinelResult {
  answer: string;
  sources?: { id: string; title: string; category: string }[];
}

/**
 * The knowledge base lives in Library's `knowledge_base` table — Q&A rows
 * (question/answer/category/is_active), not the article shape this section
 * originally invented (L51). RLS "Staff can manage" authorizes full CRUD via
 * the x-customer-token header, so direct PostgREST works. The adapter maps:
 * title ↔ question, body ↔ answer, is_published ↔ is_active. `slug`,
 * `video_url`, and `tags` have no backing columns (UI-only, not persisted).
 */
function knowledgeHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) headers["x-customer-token"] = token;
  return headers;
}

function mapKnowledgeRow(r: any): KnowledgeArticle {
  return {
    id: r.id,
    title: r.question ?? "",
    slug: r.id,
    category: r.category ?? "general",
    body: r.answer ?? "",
    video_url: null,
    is_published: !!r.is_active,
    tags: null,
    created_at: r.created_at,
    updated_at: r.updated_at,
  };
}

export async function listKnowledgeArticles(): Promise<KnowledgeArticle[]> {
  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/knowledge_base?select=*&order=category.asc,question.asc`,
    { headers: knowledgeHeaders() }
  );
  if (!res.ok) throw new Error("Failed to load knowledge articles");
  const data = await res.json();
  return (data || []).map(mapKnowledgeRow);
}

export async function saveKnowledgeArticle(article: Partial<KnowledgeArticle>): Promise<KnowledgeArticle> {
  const isEdit = !!article.id;
  const url = isEdit
    ? `${SUPABASE_URL}/rest/v1/knowledge_base?id=eq.${article.id}`
    : `${SUPABASE_URL}/rest/v1/knowledge_base`;

  const payload: Record<string, unknown> = {
    question: article.title,
    answer: article.body,
    category: article.category ?? "general",
    is_active: article.is_published ?? true,
    updated_at: new Date().toISOString(),
  };

  const res = await fetch(url, {
    method: isEdit ? "PATCH" : "POST",
    headers: { ...knowledgeHeaders(), Prefer: "return=representation" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("Failed to save knowledge article");
  const data = await res.json();
  return mapKnowledgeRow(Array.isArray(data) ? data[0] : data);
}

export async function deleteKnowledgeArticle(articleId: string): Promise<void> {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/knowledge_base?id=eq.${articleId}`, {
    method: "DELETE",
    headers: knowledgeHeaders(),
  });
  if (!res.ok) throw new Error("Failed to delete knowledge article");
}

export async function askTheSentinel(question: string): Promise<AskSentinelResult> {
  // First load published articles to perform grounded prompt query
  const articles = await listKnowledgeArticles();
  const published = articles.filter((a) => a.is_published);
  
  // Format context for staff query
  const qLower = question.toLowerCase();
  const relevant = published.filter(
    (a) =>
      a.title.toLowerCase().includes(qLower) ||
      a.body.toLowerCase().includes(qLower) ||
      a.category.toLowerCase().includes(qLower)
  );

  const matched = relevant.length > 0 ? relevant : published.slice(0, 5);
  
  if (matched.length === 0) {
    return {
      answer: "No matching SOPs or guidelines found in the knowledge base. Please check with the Bar Manager on duty.",
      sources: [],
    };
  }

  // Construct grounded response summary
  const best = matched[0];
  return {
    answer: `Based on **${best.title}** (${best.category.replace("_", " ")}):\n\n${best.body.substring(0, 450)}${best.body.length > 450 ? "…" : ""}`,
    sources: matched.map((m) => ({ id: m.id, title: m.title, category: m.category })),
  };
}

// ── Bottle & Distillery AI Enrichment ──────────────────────────────────────

export interface BottleEnrichmentQueueItem {
  id: string;
  name: string;
  category_slug: string | null;
  subcategory: string | null;
  description: string | null;
  mashbill?: string | null;
  age_years?: number | null;
  abv?: number | null;
  has_image: boolean;
}

export interface DistilleryEnrichmentQueueItem {
  id: string;
  name: string;
  slug: string;
  region: string | null;
  state: string | null;
  country: string | null;
  founded_year: number | null;
  master_distiller: string | null;
  website_url: string | null;
  story: string | null;
  has_location: boolean;
}

export async function listBottleEnrichmentQueue(): Promise<BottleEnrichmentQueueItem[]> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  // Real column names: mash_bill / age / proof (the original select used
  // invented mashbill/age_years/abv columns and 401'd — L51).
  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/menu_items?is_active=eq.true&item_type=eq.spirit&select=id,name,category_slug,subcategory,description,mash_bill,age,proof,image_url&order=name.asc&limit=250`,
    { headers }
  );
  if (!res.ok) throw new Error("Failed to load bottle enrichment queue");
  const data = await res.json();
  return (data || []).map((b: any) => ({
    ...b,
    mashbill: b.mash_bill ?? null,
    age_years: b.age ?? null,
    abv: b.proof != null ? Number(b.proof) / 2 : null,
    has_image: !!b.image_url,
  }));
}

export async function listDistilleryEnrichmentQueue(): Promise<DistilleryEnrichmentQueueItem[]> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  // Real column names: website / history_notes / latitude / longitude; there
  // is no master_distiller column (L51).
  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/distilleries?select=id,name,slug,region,state,country,founded_year,website,history_notes,latitude,longitude&order=name.asc&limit=250`,
    { headers }
  );
  if (!res.ok) throw new Error("Failed to load distillery enrichment queue");
  const data = await res.json();
  return (data || []).map((d: any) => ({
    ...d,
    master_distiller: null,
    website_url: d.website ?? null,
    story: d.history_notes ?? null,
    has_location: d.latitude != null && d.longitude != null,
  }));
}

export async function saveDistillerySpecs(id: string, specs: Partial<DistilleryEnrichmentQueueItem>): Promise<void> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  // Translate UI field names to the real columns before PATCHing.
  const { website_url, story, master_distiller: _md, ...rest } = specs as Record<string, any>;
  const payload: Record<string, any> = { ...rest };
  if (website_url !== undefined) payload.website = website_url;
  if (story !== undefined) payload.history_notes = story;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/distilleries?id=eq.${id}`, {
    method: "PATCH",
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("Failed to save distillery specs");
}

export async function saveBottleSpecs(id: string, specs: Record<string, any>): Promise<void> {
  const headers: Record<string, string> = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) headers["x-customer-token"] = token;

  // Translate UI field names to the real columns (mash_bill / age / proof;
  // `age` is text — an age statement, not a number). ABV entered in the UI is
  // stored as proof (ABV × 2).
  const { mashbill, age_years, abv, ...rest } = specs;
  const payload: Record<string, any> = { ...rest };
  if (mashbill !== undefined) payload.mash_bill = mashbill;
  if (age_years !== undefined) payload.age = age_years == null ? null : String(age_years);
  if (abv !== undefined) payload.proof = abv == null ? null : Number(abv) * 2;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/menu_items?id=eq.${id}`, {
    method: "PATCH",
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("Failed to save bottle specs");
}

export async function updateBottleShelfLocation(
  bottleId: string,
  shelfLocation: string[]
): Promise<{ ok: true; bottle_id: string; shelf_location: string[] }> {
  return callFn("bar-admin-update-shelf-location", {
    action: "update_location",
    bottle_id: bottleId,
    shelf_location: shelfLocation,
  });
}

export interface ShelfAuditRecord {
  id: string;
  action: string;
  timestamp: string;
  slot_code?: string;
  from_slot?: string;
  to_slot?: string;
  auditor_name?: string;
  auditor_id?: string;
  total_scanned?: number;
  verified_count?: number;
  added_count?: number;
  missing_count?: number;
  verified_bottles?: Array<{ id: string; name: string }>;
  missing_bottles?: Array<{ id: string; name: string; action: string }>;
  added_bottles?: Array<{ id: string; name: string; action: string }>;
  notes?: string | null;
  bottle_name?: string;
  acknowledged_by_owner?: boolean;
}

export async function batchAuditShelfSlot(payload: {
  slot_code: string;
  scanned_bottle_ids: string[];
  missing_bottle_ids: string[];
  added_bottle_ids: string[];
  notes?: string | null;
}): Promise<{ ok: true; slot_code: string; audit_entry: ShelfAuditRecord }> {
  return callFn("bar-admin-update-shelf-location", {
    action: "batch_audit_slot",
    ...payload,
  });
}

export async function listShelfAuditHistory(slotCode?: string): Promise<{ ok: true; history: ShelfAuditRecord[] }> {
  return callFn("bar-admin-update-shelf-location", {
    action: "list_audit_history",
    slot_code: slotCode || null,
  });
}

export async function smartMoveShelfBottle(payload: {
  bottle_id: string;
  from_slot: string;
  to_slot: string;
  notes?: string | null;
}): Promise<{ ok: true; move: ShelfAuditRecord }> {
  return callFn("bar-admin-update-shelf-location", {
    action: "smart_move_bottle",
    ...payload,
  });
}

export interface BarcodeMatchResult {
  bottle: any;
  variation?: {
    id: string;
    size_label?: string;
    upc?: string;
    sku?: string;
    bottle_price_cents?: number | null;
    is_default?: boolean;
  };
}

export interface BarcodeLookupResponse {
  ok: boolean;
  status: "matched" | "ambiguous" | "not_found";
  match?: BarcodeMatchResult;
  matches?: BarcodeMatchResult[];
  searched_variants: string[];
}

export async function lookupBottleByBarcode(code: string): Promise<BarcodeLookupResponse> {
  return callFn("bar-admin-update-shelf-location", {
    action: "lookup_barcode",
    code,
  });
}

export async function linkBottleBarcode(
  bottleId: string,
  upc: string,
  variationId?: string
): Promise<{ ok: true; bottle_id: string; variation_id?: string; upc: string }> {
  return callFn("bar-admin-update-shelf-location", {
    action: "link_barcode",
    bottle_id: bottleId,
    variation_id: variationId,
    upc,
  });
}

export async function forceSquareCatalogSync(): Promise<{ ok: true; result: any }> {
  return callFn("bar-admin-update-shelf-location", {
    action: "force_square_sync",
  });
}

export async function insertCustomMenuItem(item: {
  name: string;
  distillery?: string | null;
  category_slug?: string | null;
  subcategory?: string | null;
  shelf_location?: string[] | null;
  upc?: string | null;
  pour_price_cents?: number | null;
  bottle_price_cents?: number | null;
  is_active?: boolean;
}): Promise<any> {
  const token = getStoredToken();
  const headers: Record<string, string> = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
    Prefer: "return=representation",
  };
  if (token) headers["x-customer-token"] = token;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/menu_items`, {
    method: "POST",
    headers,
    body: JSON.stringify(item),
  });

  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`Failed to insert custom bottle: ${txt || `HTTP ${res.status}`}`);
  }
  const data = await res.json().catch(() => null);
  return data?.[0] || data;
}

// ── Search & SEO Telemetry ─────────────────────────────────────────────────

export interface SEOMetricsReport {
  weekly_impressions: number;
  weekly_clicks: number;
  avg_position: number;
  avg_ctr: number;
  top_queries: { query: string; impressions: number; clicks: number; position: number }[];
  top_pages: { url: string; clicks: number; impressions: number }[];
  last_synced: string;
}

export interface ReferralItem {
  id: string;
  referrer_name: string;
  referral_code: string;
  clicks_count: number;
  signups_count: number;
  rewards_earned: string;
  created_at: string;
}

export async function getSEOMetrics(): Promise<SEOMetricsReport> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/get-seo-report`, {
      method: "POST",
      headers,
      body: JSON.stringify({ weeks: 13 }),
    });
    if (res.ok) {
      const data = await res.json();
      return {
        weekly_impressions: data.latest?.impressions || 1840,
        weekly_clicks: data.latest?.clicks || 342,
        avg_position: data.latest?.position || 8.4,
        avg_ctr: data.latest?.ctr || 4.2,
        top_queries: data.gsc?.queries || [
          { query: "sentinel room kansas city", impressions: 520, clicks: 140, position: 1.1 },
          { query: "whiskey library crossroads kc", impressions: 430, clicks: 94, position: 2.3 },
          { query: "speakeasy cross roads cocktail lounge", impressions: 380, clicks: 68, position: 3.1 },
          { query: "bourbon tasting flight downtown kc", impressions: 290, clicks: 42, position: 4.2 },
        ],
        top_pages: data.gsc?.pages || [
          { url: "/", clicks: 210, impressions: 840 },
          { url: "/whiskey-library", clicks: 88, impressions: 490 },
          { url: "/c33", clicks: 32, impressions: 260 },
          { url: "/events", clicks: 12, impressions: 250 },
        ],
        last_synced: data.last_synced || new Date().toISOString(),
      };
    }
  } catch (e) {
    // Fallback telemetry
  }

  return {
    weekly_impressions: 1840,
    weekly_clicks: 342,
    avg_position: 8.4,
    avg_ctr: 4.2,
    top_queries: [
      { query: "sentinel room kansas city", impressions: 520, clicks: 140, position: 1.1 },
      { query: "whiskey library crossroads kc", impressions: 430, clicks: 94, position: 2.3 },
      { query: "speakeasy cross roads cocktail lounge", impressions: 380, clicks: 68, position: 3.1 },
      { query: "bourbon tasting flight downtown kc", impressions: 290, clicks: 42, position: 4.2 },
    ],
    top_pages: [
      { url: "/", clicks: 210, impressions: 840 },
      { url: "/whiskey-library", clicks: 88, impressions: 490 },
      { url: "/c33", clicks: 32, impressions: 260 },
      { url: "/events", clicks: 12, impressions: 250 },
    ],
    last_synced: new Date().toISOString(),
  };
}

export async function listReferrals(): Promise<ReferralItem[]> {
  const headers = {
    apikey: SUPABASE_PUBLISHABLE_KEY,
    "Content-Type": "application/json",
  };
  const token = getStoredToken();
  if (token) (headers as any)["x-customer-token"] = token;

  const res = await fetch(`${SUPABASE_URL}/rest/v1/referrals?select=*&order=created_at.desc`, { headers });
  if (res.ok) {
    return res.json();
  }
  return [];
}

export async function adminBroadcastReload(
  eventType: "mandatory_reload" | "force_update_check" = "mandatory_reload"
): Promise<{ success: boolean; event: string; broadcast_at: string; reload_id?: string | null }> {
  return callFn("bar-admin-broadcast-reload", { event: eventType });
}














export interface DistilleryOption {
  id: string;
  name: string;
  is_distillery: boolean | null;
}

/**
 * Distillery picker source for the events editor.
 *
 * `public.distilleries` has no `metadata` column — selecting one made PostgREST
 * return 400 and the old version of this helper swallowed that into `[]`, so the
 * picker looked empty. Keep the select list to real columns and surface failures.
 */
export async function listDistilleries(): Promise<DistilleryOption[]> {
  const headers: Record<string, string> = { apikey: SUPABASE_PUBLISHABLE_KEY };
  const token = getStoredToken();
  if (token) headers["x-customer-token"] = token;
  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/distilleries?select=id,name,is_distillery&order=name.asc&limit=1000`,
    { headers }
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(`Failed to load distilleries: HTTP ${res.status} ${detail.slice(0, 200)}`);
  }
  return res.json();
}



export async function saveEventPartners(eventId: string, partners: any[]) {
  const headers = { apikey: SUPABASE_PUBLISHABLE_KEY, 'Content-Type': 'application/json' } as any;
  const token = getStoredToken();
  if (token) headers['x-customer-token'] = token;
  
  const del = await fetch(`${SUPABASE_URL}/rest/v1/calendar_event_partners?event_id=eq.${eventId}`, { method: 'DELETE', headers });
  if (!del.ok) {
    const detail = await del.text().catch(() => "");
    throw new Error(`Failed to clear event partners: HTTP ${del.status} ${detail.slice(0, 200)}`);
  }
  if (partners.length > 0) {
    const ins = await fetch(`${SUPABASE_URL}/rest/v1/calendar_event_partners`, {
      method: 'POST',
      headers: { ...headers, Prefer: 'return=minimal' },
      body: JSON.stringify(partners.map((p, i) => ({ event_id: eventId, partner_id: p.partner_id, role: p.role, display_order: i })))
    });
    if (!ins.ok) {
      const detail = await ins.text().catch(() => "");
      throw new Error(`Failed to save event partners: HTTP ${ins.status} ${detail.slice(0, 200)}`);
    }
  }
}

// ── Event images ────────────────────────────────────────────────────────────
// Uploads go through the Library's staff-gated `event-image-presign` function
// (manager/owner via x-customer-token): it mints a signed upload URL with the
// service role, the browser PUTs the bytes to that URL. A direct browser POST to
// the `event-images` bucket with the publishable key is rejected by Storage RLS.
// Mirrors Sentinel/public/js/events-admin.js `uploadEventImage`.

export const EVENT_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
// Must stay a subset of what `event-image-presign` accepts (jpeg/png/webp/gif).
const EVENT_IMAGE_ALLOWED_MIMES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const EVENT_IMAGE_MIME_BY_EXT: Record<string, string> = {
  jpg: "image/jpeg", jpeg: "image/jpeg", jpe: "image/jpeg",
  png: "image/png", webp: "image/webp", gif: "image/gif",
};

/** Resolve a trustworthy image MIME for a picked file, or null if unsupported. */
export function eventImageMime(file: File): string | null {
  const browserType = (file.type || "").toLowerCase();
  if (EVENT_IMAGE_ALLOWED_MIMES.includes(browserType)) return browserType;
  // Android sometimes reports an empty / octet-stream type — fall back to the extension.
  const ext = file.name.split(".").pop()?.toLowerCase() || "";
  return EVENT_IMAGE_MIME_BY_EXT[ext] || null;
}

/** Upload an event image; resolves to the public URL to store in `image_url`. */
export async function uploadEventImage(file: File): Promise<string> {
  if (file.size > EVENT_IMAGE_MAX_BYTES) throw new Error("Image must be under 5MB.");
  const mime = eventImageMime(file);
  if (!mime) throw new Error("Unsupported image type. Please upload a JPEG, PNG, WebP, or GIF.");

  const presign = await callFn<{ upload_url?: string; public_url?: string; error?: string }>(
    "event-image-presign",
    { mime_type: mime }
  );
  if (!presign?.upload_url || !presign?.public_url) {
    throw new Error(presign?.error || "Presign returned no upload URL");
  }

  const put = await fetch(presign.upload_url, {
    method: "PUT",
    headers: { "Content-Type": mime, "x-upsert": "true" },
    body: file,
  });
  if (!put.ok) {
    const detail = await put.text().catch(() => "");
    throw new Error(`Upload failed: HTTP ${put.status}${detail ? " — " + detail.slice(0, 120) : ""}`);
  }
  return presign.public_url;
}

/**
 * URLs for the "Browse Existing" picker: every `image_url` already on an event
 * plus, best-effort, the files in the public `event-images` bucket. The bucket
 * listing is optional — Storage may refuse the publishable key — so a failure
 * there degrades to the event-derived list rather than an error.
 */
export async function listEventImageUrls(knownUrls: Array<string | null | undefined>): Promise<string[]> {
  const urls = new Set<string>();
  knownUrls.forEach((u) => { if (u) urls.add(u); });
  try {
    const headers: Record<string, string> = {
      apikey: SUPABASE_PUBLISHABLE_KEY,
      Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
      "Content-Type": "application/json",
    };
    const res = await fetch(`${SUPABASE_URL}/storage/v1/object/list/event-images`, {
      method: "POST",
      headers,
      body: JSON.stringify({ prefix: "", limit: 100, offset: 0, sortBy: { column: "created_at", order: "desc" } }),
    });
    if (res.ok) {
      const files: Array<{ name?: string }> = await res.json();
      files.forEach((f) => {
        if (f.name && !f.name.endsWith("/") && /\.(jpe?g|png|webp|gif|svg)$/i.test(f.name)) {
          urls.add(`${SUPABASE_URL}/storage/v1/object/public/event-images/${f.name}`);
        }
      });
    } else {
      console.warn("event-images bucket list refused:", res.status);
    }
  } catch (e) {
    console.warn("event-images bucket list failed:", e);
  }
  return Array.from(urls);
}

export async function loadEventPartners(eventId: string) {
  const headers = { apikey: SUPABASE_PUBLISHABLE_KEY };
  const res = await fetch(`${SUPABASE_URL}/rest/v1/calendar_event_partners?event_id=eq.${eventId}&select=partner_id,role&order=display_order.asc`, { headers });
  if (!res.ok) return [];
  return res.json();
}


export async function updateSquareCatalog(payload: any) {
  const token = getStoredToken();
  if (!token) return { ok: false };
  const res = await fetch(`${SUPABASE_URL}/functions/v1/create-checkout-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', apikey: SUPABASE_PUBLISHABLE_KEY, 'x-customer-token': token },
    body: JSON.stringify(payload)
  });
  return res.json();
}

export async function createCheckoutLink(payload: any) {
  const token = getStoredToken();
  if (!token) return { ok: false, error: "No token" };
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/create-checkout-link`, {
      method: "POST",
      headers: { apikey: SUPABASE_PUBLISHABLE_KEY, "Content-Type": "application/json", "x-customer-token": token },
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      const txt = await res.text();
      return { ok: false, error: `HTTP ${res.status}: ${txt}` };
    }
    return await res.json();
  } catch (err: any) {
    return { ok: false, error: err.message };
  }
}

export async function syncSquareCapacity(eventId: string) {
  const token = getStoredToken();
  if (!token) return { ok: false };
  const res = await fetch(`${SUPABASE_URL}/functions/v1/create-checkout-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', apikey: SUPABASE_PUBLISHABLE_KEY, 'x-customer-token': token },
    body: JSON.stringify({ action: 'sync-capacity', event_id: eventId })
  });
  return res.json();
}



export async function publishSocialEvent(eventId: string, action: string) {
  const token = getStoredToken();
  if (!token) return { ok: false };
  const res = await fetch(`${SUPABASE_URL}/functions/v1/publish-event`, {
    method: 'POST',
    headers: { apikey: SUPABASE_PUBLISHABLE_KEY, 'Content-Type': 'application/json', 'x-customer-token': token },
    body: JSON.stringify({ action, event_id: eventId })
  });
  return res.json();
}

// ── Event announcements (Library's `send-event-announcement`, staff-gated) ──
// Contract (Sentinel/supabase/functions/send-event-announcement/index.ts):
//   { event_id, audience?: 'all'|'c33'|'insiders', preview_email?, send_email?,
//     send_sms?, resend_missed_only?, action?: 'send_date_correction', correction_note? }
// A preview_email (without the correction action) sends ONLY the preview.
export interface AnnouncementPayload {
  event_id: string;
  audience?: "all" | "c33" | "insiders";
  preview_email?: string;
  send_email?: boolean;
  send_sms?: boolean;
  resend_missed_only?: boolean;
  action?: "send_date_correction";
  correction_note?: string;
}
export interface AnnouncementResult {
  ok?: boolean;
  error?: string;
  preview?: boolean;
  sent_to?: string;
  email_sent?: number;
  email_failed?: number;
  insider_scheduled?: number;
  sms_sent?: number;
  sms_scheduled?: number;
  total?: number;
}

export async function sendEventAnnouncement(payload: AnnouncementPayload): Promise<AnnouncementResult> {
  const token = getStoredToken();
  if (!token) return { ok: false, error: "Not signed in" };
  const res = await fetch(`${SUPABASE_URL}/functions/v1/send-event-announcement`, {
    method: 'POST',
    headers: { apikey: SUPABASE_PUBLISHABLE_KEY, 'Content-Type': 'application/json', 'x-customer-token': token },
    body: JSON.stringify(payload)
  });
  const data = (await res.json().catch(() => ({}))) as AnnouncementResult;
  // The function answers 4xx with { error } and no `ok`; normalise so callers
  // can branch on `ok` alone.
  if (!res.ok && data.ok === undefined) return { ok: false, error: data.error || `HTTP ${res.status}` };
  return data;
}

/** Per-event email performance from `bar-event-email-stats` (managers/owners). */
export type { EmailStats } from "./emailStats";

/** Resolves to null when the function refuses (non-managers), fails, or replies off-shape. */
export async function getEmailStats(eventId: string): Promise<EmailStats | null> {
  try {
    return normalizeEmailStats(await callFn<unknown>("bar-event-email-stats", { event_id: eventId }));
  } catch (e) {
    console.warn("bar-event-email-stats failed:", e);
    return null;
  }
}

// ── Event series (recurring events) ─────────────────────────────────────────
export interface EventSeriesOption { id: string; slug: string; title: string }

/** `public.event_series` is publicly readable (RLS "Event series are publicly readable"). */
export async function listEventSeries(): Promise<EventSeriesOption[]> {
  const headers: Record<string, string> = { apikey: SUPABASE_PUBLISHABLE_KEY };
  const token = getStoredToken();
  if (token) headers["x-customer-token"] = token;
  const res = await fetch(`${SUPABASE_URL}/rest/v1/event_series?select=id,slug,title&order=title.asc`, { headers });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(`Failed to load event series: HTTP ${res.status} ${detail.slice(0, 200)}`);
  }
  return res.json();
}

/** Create a series through the staff-gated `submit-form` `save-series` action (same as the original admin). */
export async function createEventSeries(title: string): Promise<EventSeriesOption> {
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const res = await callFn<{ ok?: boolean; data?: EventSeriesOption[]; error?: string }>("submit-form", {
    action: "save-series", title, slug, status: "active",
  });
  const created = res?.data?.[0];
  if (!res?.ok || !created) throw new Error(res?.error || "Series create failed");
  return { id: created.id, slug: created.slug, title: created.title };
}

// ── Offline totals ──────────────────────────────────────────────────────────
/** Save cash/card donations + manual headcount for an event (routes through save-event). */
export async function saveEventOfflineTotals(
  eventId: string,
  totals: { offline_cash_cents: number; offline_card_cents: number; manual_attendee_count: number }
): Promise<void> {
  await saveAdminEvent({ id: eventId, ...totals });
}

// ── Square ticket sync ──────────────────────────────────────────────────────
export interface SquareTicketSyncResult {
  error?: string;
  registrations_created?: number;
  matched?: number;
  skipped_existing?: number;
}
/** Backfill registrations from Square orders (`purchase-event-ticket` admin action). */
export async function syncSquareTickets(eventId: string): Promise<SquareTicketSyncResult> {
  return callFn<SquareTicketSyncResult>("purchase-event-ticket", { action: "admin-backfill-tickets", event_id: eventId });
}

// ── Social publish credentials ──────────────────────────────────────────────
export interface PublishCredentials {
  gbp: boolean;
  facebook: boolean;
  instagram: boolean;
  facebook_error?: string | null;
  facebook_error_code?: "expired" | "scope" | string | null;
}
/** Which social targets are configured — cached by the caller for the session. */
export async function checkPublishCredentials(): Promise<PublishCredentials> {
  const fallback: PublishCredentials = { gbp: false, facebook: false, instagram: false };
  try {
    const res = await callFn<{ credentials?: PublishCredentials }>("publish-event", { action: "check-credentials", event_id: "check" });
    return { ...fallback, ...(res?.credentials || {}) };
  } catch (e) {
    console.warn("publish-event check-credentials failed:", e);
    return fallback;
  }
}


/* ── Pour counts (bar-pour-count) ────────────────────────────────────────── */

export type PourFillLevel = "empty" | "quarter" | "half" | "three_quarters" | "full";

export interface SetPourCountResult {
  ok: true;
  pour_item_id: string;
  name: string;
  /** As saved. null means sealed bottles are in stock but no bottle is open. */
  pours_remaining: number | null;
  /** As saved; a database trigger can set out_of_stock even when pours were requested. */
  status: string | null;
  /** As saved. Send it back as `seen.pour_count_version` on the next save from this screen. */
  pour_count_version: number;
  /** The count written to Square. */
  pours_set: number;
  /** A full bottle as used for a fill level; null when an exact count needed no size. */
  pours_per_bottle: number | null;
  /** Size behind "Full" — show it before confirming ("Full = 25 pours, 750 ML"). */
  bottle_size_label: string | null;
  sealed_bottle_count: number;
  square_synced: true;
  app_updated: true;
  /** true: a Bottle Opened comp rang around the save. null: that could not be checked. false: none. */
  comp_rang: boolean | null;
  /** Set whenever comp_rang is not false; show it and ask staff to re-check the bottle. */
  notice: string | null;
  /** false when the count saved but its audit row did not; worth surfacing to a manager. */
  audit_logged: boolean;
}

/**
 * Set how many 1 oz pours are left in the open bottle. Writes Square first, so the
 * count survives the 15-minute inventory sync; pass either a fill level or an exact
 * number. Pass `seen`: the pours_remaining and status the screen is showing for this pour (after
 * a save, the ones this function returned). If the pour no longer holds them, nothing is written.
 * Leave request_id to its default: a fresh UUID for every attempt, retries included.
 * A repeated count is harmless (it is absolute), and reusing an id cannot succeed because each
 * attempt is timestamped, so Square answers "already sent".
 *
 * On success, show `notice` whenever it is set: the save went through, but a Bottle Opened comp
 * rang at the same moment (comp_rang true) or that could not be checked (comp_rang null).
 *
 * Failures from the endpoint throw ApiError (see callFn); its message is written for staff.
 *  - 422: pour not linked to an active bottle, no sealed count yet, or a fill level when the
 *    bottle size is unknown (several sizes, an unreadable size such as "Regular", minis only,
 *    none on record). Offer an exact number; Empty always works.
 *  - 409 or 500 with `error.saved === true`: Square has the count but the app row did not
 *    change (another change landed, or the app write failed). Refresh; the app catches up
 *    within 15 minutes.
 *  - 409 with `error.changed === true`: the count or status changed after the screen loaded it
 *    (another save, a comp, a sale), so nothing was written. Refresh, show the new count, and
 *    let staff set it again. This is also what a stalled request gets if a retry saved first.
 *  - 409 with `error.busy === true`: another save for this bottle was still in flight, so nothing
 *    was written. Wait a moment, refresh, and let staff set it again. Single-flight your own save
 *    (one request at a time per screen) and this only ever means a second tablet.
 *  - 409 without `saved`, `changed` or `busy`: this exact request was already sent.
 *  - 502: Square refused, or did not confirm; the app was not changed.
 *  - 500 without `saved` and the message "Couldn't load this bottle…": nothing was written.
 *    Any OTHER 500 without `saved` (the catch-all "Internal error") is unknown — Square may
 *    already have the count; refresh before retrying.
 *  - 503: the request stalled before reaching Square; nothing was written.
 *  - 400: a bad request from the screen (a bug, not a staff action). 401: not signed in with a
 *    shelf-map role. 404: the pour is gone or no longer active. Nothing was written for any.
 *  - A 502 or 504 whose message is only "bar-pour-count failed (HTTP 5xx)" came from the
 *    gateway, not this function: unknown whether Square has the count; refresh before retrying.
 * The request itself can also fail before any answer: ApiError 408 after callFn's 20 s timeout,
 * or a plain network Error. In both cases Square may or may not have the count — refresh the
 * bottle before trying again, and show your own wording, not the raw message.
 */
export async function setPourCount(
  pourItemId: string,
  amount: { fill: PourFillLevel } | { pours: number },
  seen: { pour_count_version: number },
  requestId: string = crypto.randomUUID()
): Promise<SetPourCountResult> {
  return callFn("bar-pour-count", {
    action: "set_count",
    pour_item_id: pourItemId,
    request_id: requestId,
    expected_pour_count_version: seen.pour_count_version,
    ...amount,
  });
}

/** One pour row attached to its bottle (bar-pour-count `list_pours`, Phase 3c). */
export interface BottlePour {
  pour_item_id: string;
  bottle_item_id: string | null;
  pour_name: string;
  pours_remaining: number | null;
  pour_status: string | null;
  /** Send back as `seen.pour_count_version`. The browser role has no SELECT on this column, which
   *  is why pour state cannot be read straight from `menu_items` the way the shelf map reads the
   *  rest of its data. */
  pour_count_version: number;
  /** false when the pour has no Square variation. The full check still runs on the write. */
  actionable: boolean;
  /** The open `empty_check` to resolve when staff mark this bottle empty, so the shelf map and
   *  the bottle-checks screen clear the same row. null when there is none: then "empty" is an
   *  ordinary count of 0 through `setPourCount`, which does NOT close any check. */
  open_empty_check_id: string | null;
  /** false when the open-check lookup itself failed. Empty still zeroes Square, but do not tell
   *  staff a check was cleared. */
  open_check_known: boolean;
}

/**
 * Pour state for up to 100 bottles at a time, keyed by the bottle each pour really belongs to
 * (the service role can read `bottle_id`; the browser cannot, and falls back to name matching).
 * Ask for the bottles about to be acted on, not a whole grid — the version is only worth having
 * while it is fresh, and a stale one costs a 409 `changed` and a re-tap.
 */
export async function listPoursForBottles(bottleItemIds: string[]): Promise<BottlePour[]> {
  if (bottleItemIds.length === 0) return [];
  const res = await callFn<{ ok: true; pours: BottlePour[] }>("bar-pour-count", {
    action: "list_pours",
    bottle_item_ids: bottleItemIds,
  });
  return res?.pours || [];
}

/* ── Bottle checks (bar-pour-count list_checks / confirm_empty) ──────────── */

/** One open row of public.pour_checks, joined to what the screen needs to paint it. */
export interface PourCheck {
  check_id: string;
  /** "empty_check": the sync thinks this bottle is finished. "comp_reminder": a bottle was
   *  poured down without Bottle Opened for Service being rung. */
  kind: "empty_check" | "comp_reminder" | string;
  opened_at: string;
  pour_item_id: string;
  /** null when the pour went inactive or merged after the check opened (`actionable` is false). */
  pour_name: string | null;
  pours_remaining: number | null;
  pour_status: string | null;
  /** Send back as `seen.pour_count_version`. null when the pour is gone. */
  pour_count_version: number | null;
  bottle_item_id: string | null;
  bottle_name: string | null;
  /** Shelf pins. Both null and [] mean "not on the shelf map" — 483 bottles hold [] today. */
  shelf_location: string[] | null;
  /** Sealed bottles in reserve. 0 is a real, common value; only null means "unknown". */
  sealed_bottle_count: number | null;
  /** Allocated, limited or a Sentinel pick — worth naming before anyone zeroes it. */
  flagged: boolean;
  /** What the sync saw when it opened the check. Often negative; 0 is meaningful. */
  square_pour_count_at_open: number | null;
  sealed_count_at_open: number | null;
  /** Newest recorded pour sale. null is normal — three of the seven live checks have none. */
  last_sale_at: string | null;
  /** false when the pour no longer exists: show the row, offer no actions. */
  actionable: boolean;
}

/** Open bottle checks, oldest first. An empty list is the normal state. */
export async function listPourChecks(): Promise<PourCheck[]> {
  const res = await callFn<{ ok: true; checks: PourCheck[] }>("bar-pour-count", { action: "list_checks" });
  return res?.checks || [];
}

export interface ConfirmPourEmptyResult {
  ok: true;
  check_id: string;
  /** "confirmed_empty" normally; another tablet's resolution if it got there first. */
  resolution: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  /** Present only when this call did the work; absent on an already-resolved check. */
  already_resolved?: true;
  pour_item_id?: string;
  name?: string;
  pours_remaining?: number | null;
  status?: string | null;
  pour_count_version?: number;
  /** false when the count was set but the shelf pin could not be cleared. */
  shelf_cleared?: boolean;
  square_synced?: true;
  app_updated?: true;
  audit_logged?: boolean;
}

/**
 * Confirm a flagged bottle really is empty: Square is set to 0 first, then the app row, then the
 * check is resolved and the shelf pin cleared. Pass the `pour_count_version` the screen painted.
 *
 * Idempotent: a second tap, or another tablet that got there first, comes back `already_resolved`
 * with the resolution that stands and pushes nothing to Square.
 *
 * Failures throw ApiError with staff-readable messages; on top of the cases listed on
 * {@link setPourCount}:
 *  - 409 with `error.busy === true`: another save for this bottle was still in flight, so nothing
 *    was written. Wait, refresh, try again. (The screen's own single-flight should make this rare;
 *    it catches a second tablet on the same function instance.)
 *  - 422: the row is a comp reminder, not an empty check — ring Bottle Opened for Service instead.
 *  - 404: the check is gone.
 */
export async function confirmPourEmpty(
  checkId: string,
  seen: { pour_count_version: number },
  requestId: string = crypto.randomUUID()
): Promise<ConfirmPourEmptyResult> {
  return callFn("bar-pour-count", {
    action: "confirm_empty",
    check_id: checkId,
    request_id: requestId,
    expected_pour_count_version: seen.pour_count_version,
  });
}

// ── Who worked (bar-cash-day-staff): Square clock-ins for a cash business day ──

export interface OnShiftPerson {
  name: string;
  /** Present for managers/owners only (the function is manager-gated). */
  phone: string | null;
  job_titles: string[];
  shifts: Array<{ start_at: string; end_at: string | null }>;
}

export function getCashDayStaff(businessDate: string): Promise<{ business_date: string; staff: OnShiftPerson[] }> {
  return callFn("bar-cash-day-staff", { business_date: businessDate });
}
